'use client';

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { pathForLang } from '@/lib/i18n';
import { useTranslation } from '@/components/TranslationProvider';
import {
  MOBILE_BREAKPOINT,
  elbowConnector,
  getCircleLayout,
  pointOnCircle,
  pointsToPath,
  polylineLength,
  rectRadiusAlong,
  topicHitRadius,
} from './divergenceGeometry';

const TAU = Math.PI * 2;

// ─── Tuning ──────────────────────────────────────────────────────────────────
// Everything visual is driven from here. Lengths are fractions of the ring
// radius unless noted, so they hold at every size.
const TUNING = {
  /** Seed for the grain scatter. */
  seed: 7,

  /** Where the `intensity` prop sits when nobody sets it. Just under half: the
   *  ring should look alive and considered at rest, with room to go both
   *  quieter and much louder. */
  intensity: 0.45,
  /** What intensity does to the wave's size and to its rate. Both are
   *  multipliers around the middle setting, so 0.45 leaves the ring exactly as
   *  tuned and the ends of the range are a genuine change rather than a nudge. */
  intensitySwing: 1.5,
  intensityRate: 0.9,

  /* ── The rim ──────────────────────────────────────────────────────────────
     There is no circle drawn here. There used to be: an exact <circle>, a
     hairline of it, with a scatter of dots either side for texture — and what
     you read was the line, with some grit near it. The ring is the particles
     now, and nothing else. Take them away and there is nothing there.

     What is left of it is deliberately faint. It was carrying most of the
     ring's weight, and a painted layer is a layer that cannot move — so the
     line down the middle of the band was the one part of the ring that was
     always exactly where it had been, however much the cloud around it drifted.
     Most of that weight is in the live particles now, which do move; this is a
     shadow under them, enough that the circle never breaks and not so much that
     it reads as a drawn stroke.

     Which means the particles have to carry the weight a stroke used to. They
     are laid down in several passes at a fixed alpha each, and the darkness in
     the middle is overlap rather than opacity: a scatter concentrated hard
     toward the ring line, so the core fills in solid and the edges break up
     into grain on their own. The thickness varies around the circumference and
     the variation is fixed, not animated — this layer is painted once per
     layout and never touched again, which is what keeps it free. What moves
     lives on the two layers above it. */
  ringDotCount: { desktop: 2600, mobile: 2200 },
  ringDotLayers: 4,
  /** Full width of the rough band, in px, centred on the ring line. Wide,
   *  because the cubic bias below keeps the core tight regardless: half the
   *  particles land within a couple of pixels of the line and the rest carry
   *  out into a thinning scatter. That scatter is this layer's real job — it
   *  is drawn at device resolution and costs nothing per frame, so it is the
   *  right place for every grain that does not strictly need to move. */
  ringDotScatter: 23,
  ringDotSize: 0.26,
  ringDotSizeRange: 0.34,
  ringDotAlpha: 0.3,
  /** How much of the fraying happens on the inside. An ink line laid on a page
   *  frays outward and stays clean against the space it encloses; doing it
   *  evenly both ways puts grit inside the circle, where the reading is.
   *
   *  Held low on purpose. What the circle is meant to be is a calm empty disc
   *  with a narrow hard contour on it and everything unstable happening
   *  outward from there — so the inner edge is nearly a line and the outer one
   *  is where the band is allowed to come apart. */
  ringDotInner: 0.22,
  /** Per-angle thickness of the rim: a few fixed harmonics, so it is worked
   *  and uneven the way an ink line is, and thin in some places without ever
   *  breaking. */
  ringWaves: [
    { amplitude: 0.26, frequency: 3, phase: 0.9 },
    { amplitude: 0.18, frequency: 7, phase: 2.4 },
    { amplitude: 0.12, frequency: 13, phase: 5.1 },
  ],
  /** Soft blobs drifting around the band — the smoke/shadow halo.
   *
   *  There were 380 of them, each a disc with a straight falloff, drawn at full
   *  resolution with nothing softening them. At this alpha you could count
   *  them: the halo read as a scattering of grey bubbles sitting beside the
   *  ring rather than as smoke coming off it. A blob you can pick out
   *  individually is not a blob, it is a circle.
   *
   *  What fixed it was not making them smaller — that only makes smaller
   *  circles. It was giving them a gaussian falloff with no edge to find, twice
   *  the count so they overlap rather than tile, and a blur on the way to the
   *  screen (see smokeScale). They are wider than they were and half as strong
   *  each; what you see is the overlap, not any one of them. */
  smokeCount: { desktop: 330, mobile: 165 },
  smokeAlpha: 0.02,
  smokeBlob: 14,
  smokeBlobRange: 16,
  /** The halo has a canvas of its own, this fraction of the display size, which
   *  CSS stretches back up to full size. The stretch is a bilinear filter and a
   *  bilinear filter is a blur, so the particles arrive on screen merged into
   *  smoke instead of stacked as discs — and it happens during compositing,
   *  which means the blur is free and there is no full-size blit in JavaScript
   *  at all.
   *
   *  A real `filter: blur()` was tried instead and is the wrong trade here:
   *  prettier, and it re-blurs a 780x522 layer on every frame the canvas
   *  changes, which measured at nearly twice the frame cost.
   *
   *  Around a fifth is the useful range. Smaller blurs harder but starts to
   *  show the filter's own grid as soft square edges; larger stops merging
   *  anything. */
  smokeScale: 0.22,
  /** How much of the frame before is taken away each time, rather than
   *  clearing. What is left is a short wake behind every blob, so the halo you
   *  see is several frames deep and a moving particle draws a line instead of
   *  a dot. Most of what makes this read as smoke rather than as points being
   *  redrawn comes from here, and on a canvas this small it is free.
   *
   *  It also sets the halo's strength: the running total settles at about
   *  1/smokeTrail times what one frame draws, so a change here has to be paid
   *  back in smokeAlpha above or the whole ring gets darker. */
  smokeTrail: 0.46,
  smokeDrift: 6,
  smokeDriftRange: 10,
  /** How far a blob sways *along* the ring, in px of arc, on top of the flow. */
  smokeSwirl: 13,
  /** …and the flow itself: a steady drift around the circumference, in radians
   *  a second, sheared per particle so the band shears rather than turning as
   *  one piece. Every particle wraps at a whole turn, so nothing jumps however
   *  long the page has been open. */
  smokeFlow: 0.055,
  /** How far out blobs are scattered, and how far in they may start. */
  smokeSpread: 40,
  smokeInset: 9,
  /** The halo's depth, in px — the sigma of a gaussian falloff measured from
   *  the ring line. Each blob's alpha follows it, so the shadow dissolves
   *  into nothing instead of meeting a clip edge. */
  smokeBand: 26,

  /* ── The wave ─────────────────────────────────────────────────────────────
     Three fields run round the circumference, all of them functions of angle
     and time alone, and between them they do everything the ring does.

     The swell is a few low harmonics travelling at different rates and in
     different directions. It pushes the band outward and thickens it, so an
     arc rises and settles back while the arc beside it is still on its way up —
     a wave propagating round the ring, never the whole circle pulsing at once.
     The frequencies are integers, so the field closes on itself exactly and
     there is no seam at angle zero at any moment. */
  smokeSwell: [
    { frequency: 2, speed: 0.1, amplitude: 0.55 },
    { frequency: 3, speed: -0.07, amplitude: 0.33 },
    { frequency: 5, speed: 0.05, amplitude: 0.2 },
  ],
  /** How far the swell lifts the band, in px, and how much it thickens it. */
  smokeSwellPush: 6,
  smokeSwellDepth: 0.42,
  /* ── The perimeter's weight, arc by arc ───────────────────────────────────
     The band is not the same all the way round. Some arcs are a broad soft
     sweep, some are a brushstroke and little else, and the haze around it
     thickens and thins with them. It is fixed — the same every load — and it is
     the only thing about the perimeter that is: nothing stands on the ring
     until you point at it.

     Positions are clock hours, twelve at the top running clockwise, and the
     values are blended between neighbours so no arc has an edge where the next
     one starts. */
  arcWeight: [
    { at: 9.0, band: 1.15, haze: 1.3 },
    { at: 10.0, band: 1.28, haze: 1.4 },
    { at: 11.0, band: 1.4, haze: 1.35 },
    { at: 12.0, band: 1.3, haze: 1.25 },
    { at: 1.5, band: 1.18, haze: 1.1 },
    { at: 2.2, band: 1.26, haze: 1.3 },
    { at: 3.0, band: 1.12, haze: 1.15 },
    { at: 4.5, band: 1.0, haze: 0.95 },
    { at: 5.5, band: 0.86, haze: 0.62 },
    { at: 6.5, band: 0.84, haze: 0.58 },
    { at: 7.4, band: 1.3, haze: 1.4 },
    { at: 8.2, band: 1.22, haze: 1.3 },
  ],

  /* ── Anomalies ────────────────────────────────────────────────────────────
     Every so often something ripples outward from a point on the circumference
     and dies away. They are the only events on a resting ring, and they are
     what keeps it from reading as a loop: each one is born at its own place, at
     its own moment, travels a little way round as it fades, and none of the
     lifetimes divide into each other, so the pattern never comes back.

     Cheap, because an anomaly is not an object — it is a term added to the
     swell field for the few seconds it is alive. */
  anomalies: 5,
  /** How long one lives, in seconds, and how far its bump reaches along the
   *  circumference in radians. */
  anomalyLife: 7.5,
  anomalySpan: 0.34,
  /** How hard it pushes the band outward at its peak, in px. */
  anomalyPush: 13,

  /** What a peak is made of: how many lobes its outline has, how sharply each
   *  comes to a point, how much erosion is cut across it, how far it may lean
   *  off the radius as a share of its own half-width, and how much arc it
   *  claims relative to its own width.
   *
   *  That last one is what makes a needle possible. A spire a pixel across
   *  owns a pixel of band, which is a dozen particles, which is not a needle —
   *  it is nothing. The thin kinds claim a stretch far wider than they are and
   *  pack all of it into their own narrow base. */
  peakKinds: {
    /** The tower. One lobe, and a flank that caves in hard on the way up: a
     *  wide flared foot drawing into a slender tapering shaft, which is the
     *  silhouette in the reference and is nothing like a cone. Narrow, so
     *  `wide` pulls its base in well under the others. */
    tower: { lobes: 1, point: 2.6, erode: 0.09, lean: 0.12, claim: 4.2, wide: 0.62 },
    /** The ground it stands on. Rounded, broad, low, and heavily eroded — the
     *  point exponent under one is what bows the flanks outward into a dune
     *  instead of drawing them into a peak. */
    mound: { lobes: 3, point: 0.78, erode: 0.36, lean: 0.35, claim: 1.15, wide: 2.9 },
    dune: { lobes: 4, point: 0.62, erode: 0.46, lean: 0.3, claim: 1.1, wide: 3.1 },
  },
  /** Which kind each rank of the massif is. Rank zero is the marker itself and
   *  is the tower; everything outward from it is the ground it stands on,
   *  falling away into the band. One tall thing and a shoulder of dunes, rather
   *  than a stand of similar spikes — which is the difference between the
   *  reference and a row of conifers. */
  massifRanks: ['tower', 'mound', 'dune', 'dune', 'dune'],

  /* ── The massif ───────────────────────────────────────────────────────────
     What rises when you point at a marker, and only then. At rest the ring
     wears no formations at all — it is a band of particles and nothing else.

     It is not one peak. A row of triangles is not a mountain range, and neither
     is a single triangle: what makes the reference read as geology is that
     several summits stand together, none of them the same as its neighbour.
     So a handful of formations are raised side by side, tallest in the middle
     and falling away, each with its own width, height, lean, flanks and
     notches — and where two overlap, whichever has the stronger hold of the
     band there takes it, so the group comes out as one compound silhouette
     with saddles between its summits rather than as separate cones crossing.

     All of it is built from the particles that were already lying on that
     stretch of the ring. Nothing is spawned and nothing is drawn over the top:
     the band visibly thins where the material came from, and when you look
     away it all falls back. */
  massifPeaks: 7,
  /** How many rounded lobes make up one formation's outline.
   *
   *  A summit whose profile is one falling line is a triangle, and a triangle
   *  is the one thing the reference never is: its formations are lobed, the way
   *  an ink blot or a head of cauliflower is lobed — a full round mass in the
   *  middle with smaller ones bulging off its flanks and a concave notch pinched
   *  in between every pair. So the profile is the tallest of several elliptical
   *  caps rather than a single slope. An ellipse is what gives the lobe its
   *  belly and its near-vertical shoulder; taking the tallest of them is what
   *  cuts the notches, because in the gap between two caps neither is high.
   *
   *  Four: a crown, one on each flank, and a fourth placed anywhere, which is
   *  what stops every formation coming out symmetrical. */
  massifLobes: 3,
  /** How the flank of a lobe runs between its foot and its point.
   *
   *  One is a straight line — a triangle. Below one the flank bows outward and
   *  the lobe fattens into a dome, which is where this started and which reads
   *  as a blob however sharp the very tip is. Above one it caves inward: a
   *  flared skirt at the foot drawing up into a narrow summit, which is what a
   *  mountain looks like and what a blob never does.
   *
   *  Raising every lobe to a power would cost a power a particle. It does not:
   *  a power is monotonic, so the lobes are compared with their heights taken
   *  to the inverse power and the whole profile is raised once at the end. */
  lobePoint: 1.18,
  /** Half-width of the crown lobe, as a share of the base — under a half, so
   *  there is room outside it for the flank lobes to stand clear rather than
   *  being swallowed. */
  lobeCrown: 0.55,
  /** Where a flank lobe sits and how wide it is, both as a share of the base.
   *  Centre plus half-width stays inside 1, so every lobe closes before the
   *  base does and the formation still meets the band at nothing. */
  lobeFlank: 0.55,
  lobeFlankRange: 0.14,
  lobeWidth: 0.36,
  lobeWidthRange: 0.12,
  /** …and how tall a flank lobe is against the crown. */
  lobeRise: 0.6,
  lobeRiseRange: 0.3,
  /** Angular distance from one rank of shoulders to the next, in radians.
   *
   *  The principal summit stands at rank zero — exactly on the marker — and
   *  the shoulders step away from it in pairs, right and left alternately, so
   *  whatever else varies, the middle of what rises is always the thing you
   *  are pointing at.
   *
   *  It was an even spread across a fixed width before, and that put no summit
   *  on the marker at all: with four of them the two nearest sat a third of the
   *  way out on either side, and what stood up was a pair of clumps with the
   *  marker in the gap between them. */
  massifStep: 0.135,
  /** …and how far a shoulder may sit off its rank, so the ranks are not a
   *  ruler. The principal summit is exempt: it is never moved off the marker. */
  massifJitter: 0.016,
  /** What share of its height each rank keeps of the one inside it. Under a
   *  half the group is a spike with debris at its foot; over three quarters it
   *  is a ridge with no summit. */
  massifFall: 0.56,
  /** …and what share of the width, which falls more slowly than the height
   *  does. A foothill is squat; it is not a scale model of the summit. */
  massifNarrow: 1,
  /** How much wider than a shoulder the principal summit's own base is. This is
   *  the number that decides whether the thing at the marker is a mountain or a
   *  spike, and it wants to be generous: at a half-width under a fifteenth of a
   *  radian the summit is taller than it is broad, which is a needle. */
  massifCrown: 1,
  /** Half-width of one summit's base, in radians. Neighbours overlap at these
   *  widths, which is what makes the group one compound silhouette with saddles
   *  cut into it rather than a row of separate cones standing side by side. */
  spikeWidth: 0.026,
  spikeWidthRange: 0.016,
  /** Height of the principal summit, as a fraction of the room out to the
   *  layer's bound — all of it, so the mountain is as tall as the space allows
   *  and its crown is the part that dissolves. The shoulders take their share
   *  of that from massifFall, varied per summit by this range. */
  spikeScale: 1,
  spikeScaleRange: 0.55,
  /** How much of its height erosion takes away, cut as a few notches across
   *  each flank rather than an even shave, so the slopes are broken. */
  spikeErode: 0.3,
  /** How many notches that is, across a base. */
  spikeRipple: 7.5,
  /** How much wider than the stretch it *owns* a formation reaches for material.
   *
   *  These are two numbers and they were one, which is what made the ring go
   *  chaotic under the pointer. A tower is a fiftieth of a radian across and has
   *  to own a good deal more than that or it gets no particles at all — but
   *  owning more was also reaching further, and at seven times its width times a
   *  catchment of thirteen it was hauling particles a third of the way round the
   *  circle to build itself. What you saw was not a peak forming, it was the
   *  band being dragged sideways.
   *
   *  Ownership is `claim` on the kind. This is only how far past that it may
   *  reach, and just over one keeps every particle's journey short: material
   *  comes from beside the formation, never from across the ring. */
  /** How much wider than its base a summit's catchment is. Everything inside it
   *  is material the summit builds with, so this is what decides how dark it
   *  comes out: the ring holds about ten particles per pixel of circumference,
   *  and a summit drawing only on its own base has well under one per pixel of
   *  its own area to fill itself with.
   *
   *  Scaled by the summit's height as well as its width, because what a
   *  formation has to fill is an area and what it draws from is a length. With
   *  a catchment set by width alone, a row of summits of different heights
   *  comes out at different densities — the short ones pack their material into
   *  a small area and go black, the tall ones spread the same amount over twice
   *  as much and go grey — which reads as the big ones being unfinished. */
  spikeCatch: 1.5,
  /** How much of the ground beyond every base still belongs to whatever is
   *  tallest nearby, as a share of a summit's claim over its own base. Nothing
   *  at all and the outskirts flicker between neighbours; too much and the
   *  contest goes back to being decided by height alone. */
  spikeReach: 0.02,
  /** How much of that catchment commits outright rather than being part-taken.
   *  Left as a plain ramp, most of the material never quite arrives — it hangs
   *  half way between where it was resting and where it is wanted, and what
   *  stands there is a smear. */
  spikeGrip: 1.25,
  /** How far a summit leans off the radius, as a share of its own half-width.
   *  Almost none of them stand straight up — except the principal one, which
   *  is exempt: its crest is what the marker has to sit under, and a lean this
   *  size moves a crest twenty pixels off the thing it belongs to. */
  spikeLean: 0.8,
  smokePeakDepth: 0.7,
  /** How much heavier the halo is under a formation.
   *
   *  The reference's masses are not stipple alone. They are stipple over a
   *  wash: a soft dark ground with the grain sitting on top of it, which is
   *  what lets a small form read as solid without needing a particle in every
   *  pixel of it. Fifteen thousand particles spread round a ring cannot fill a
   *  twenty-pixel knob on their own — but they do not have to, if there is tone
   *  underneath them. This is that tone, and it arrives and leaves with the
   *  formation because it is read from the same field. */
  smokePeakInk: 1,
  /** How far up a spike the *body* of the smoke follows, as a fraction of the
   *  room available. Under half: a spike is dark and smoky at its base and
   *  pure dust by the tip, which is what makes it taper. */
  smokeBodyLift: 0.55,
  /** The secondary pulse: the whole circumference easing out and back, in px
   *  and in cycles a second. Slower than anything travelling round it, so the
   *  two never beat against each other. */
  smokeBreath: 2.6,
  smokeBreathRate: 0.11,
  /** Density round the ring: a slow travelling wave of its own, so some arcs
   *  come out almost clean and others thick and worked. */
  smokeDensity: { base: 0.5, wave: 0.75, frequency: 3, speed: 0.037 },

  /* ── The live field ───────────────────────────────────────────────────────
     Everything that moves is one population of particles with positions and
     velocities that persist between frames, and it is the same population
     throughout: the loose grain around the rim, the drifts the field raises,
     the column that stands up under the cursor. Nothing appears and nothing is
     replaced — a particle in the column is a particle that was in the band a
     moment ago and will be again.

     That is a different thing from what was here before, which computed every
     particle's position from the clock each frame. Positions from a clock have
     no memory, so nothing can be pushed, nothing can be recruited, and a pillar
     can only be faded in over the top of a field that never notices. A pillar
     that is drawn rather than gathered always reads as drawn.

     Underneath it the rim is still painted once and never touched: that layer
     is the dense heart of the band, and it is what lets this one be sparse
     enough to simulate. The reference has the same two things in it — a dark
     continuous core and a loose cloud above it, with the tower rising out of
     the cloud. */
  liveCount: { desktop: 13600, mobile: 7400 },
  /** Resolution of the layer this is drawn on, capped — see the note by the
   *  rim about where sharpness actually comes from. */
  liveDprCap: 1,
  liveAlpha: 0.78,
  liveSize: 0.6,
  liveSizeRange: 0.8,
  /** Where the cloud rests, in px from the ring line. It starts outside the
   *  painted core rather than on top of it, and most of it lies outward. */
  liveInner: 0,
  liveSpread: 19,
  liveInward: 0.3,
  /** Spring toward wherever a particle is currently meant to be, and the
   *  damping that stops it ringing. Together they are a little under critical,
   *  so a particle arrives with a small overshoot rather than easing to a halt
   *  — which is what stops the motion looking interpolated. */
  liveSpring: 62,
  liveDamp: 11,
  /** Per-particle drift, so a field at rest is not a field asleep. */
  liveDrift: 3.4,
  liveDriftRate: 0.5,
  /** How fast the cloud turns around the ring, in radians a second. Particles
   *  held in a column stop flowing: something being gathered is not travelling
   *  past at the same time. */
  liveFlow: 0.03,

  /* ── The cursor ───────────────────────────────────────────────────────────
     A circle of influence with nothing drawn for it. What tells you where it is
     is entirely what the particles near it do — they are pushed off it and
     around it, and the push falls away smoothly to nothing at the edge, so
     there is no boundary anywhere for the eye to find.

     The tangential share is what makes it read as something the field flows
     around rather than something that blows a hole in it. */
  cursorRadius: 118,
  cursorPush: 1500,
  cursorSwirl: 0.22,
  /** How fast the influence point chases the pointer, per second. Below one so
   *  the field is answering where the cursor was a moment ago, which is most of
   *  what gives the interaction weight. */
  cursorEase: 12,

  /* ── Raising it ───────────────────────────────────────────────────────────
     Not drawn. Gathered.

     When a marker lights up, the particles already lying along that stretch of
     the ring are given somewhere else to be — a place in one of the massif's
     summits — and the same spring that was holding them in the band carries
     them there. The band visibly thins where they left. Let go and the places
     disappear, the original targets come back, and they fall home. */
  /** How long the whole massif is called up or dismissed over, in ms. While it
   *  is moving the texture runs at the display's rate rather than its own
   *  twenty: a rise this short at twenty is five frames and you can count
   *  them. */
  pillarRiseMs: 260,

  /* ── Peaks ────────────────────────────────────────────────────────────────
     What makes a ridge a mountain rather than a swelling.

     A swelling is the band pushed outward: every particle keeps its place
     along the arc and simply stands further out, so the shape is as wide at
     the top as at the bottom. A peak is the band gathered *inward* as it goes
     up — the higher a particle climbs the nearer the crest it is drawn — and
     what stands there is a triangle. The two cost the same; only this is the
     difference. */
  /** How much of its resting scatter a climbing particle leaves behind. A peak
   *  built out of particles that kept a thirty-pixel spread would be a fuzzy
   *  triangle; leaving it below is what gives the flanks an edge. */
  peakShed: 0.8,
  /** How far a particle at the summit strays off the slope's clean line, in px.
   *  Nothing at the base. */
  peakFray: 2.2,

  /* ── What the material is asked to do ─────────────────────────────────────
     A formation is not one job. Three quantities below split the particles it
     gathers into the ones that make its outline, the ones that fill it, and the
     ones that hang in the air around it — and a fourth decides how far out it
     is allowed to reach for them at all.

     All of it is the band's own material. Nothing is spawned, nothing is drawn
     over the top, and every particle keeps the identity it had, so when the
     formation goes there is nothing left over to clean up: the outline, the
     body and the dust all simply go back to being band. */
  /** The share laid on the formation's surface rather than into its body.
   *
   *  A triangle filled evenly with particles has no outline. What you read is a
   *  gradient with a dark middle, and against a white page that is a smudge,
   *  not a mountain — the reference's peaks are legible because the ink is
   *  heaviest exactly along the silhouette. So a little under a third of what a
   *  formation gathers is laid on its surface instead, just under the skin,
   *  tracing the same eroded profile the flanks are cut from. The interior
   *  stays as dense as it was; the edge stops being where the density happens
   *  to run out and becomes a line. */
  crustShare: 0.45,
  /** How far under the surface that shell may sit, as a share of the height at
   *  that point. Not zero: a shell exactly on the profile is a drawn stroke. */
  crustSink: 0.14,
  /** How fast a surface particle commits.
   *
   *  This is what decides whether there is an outline at all. Every other
   *  particle is moved a fraction of the way to where it is wanted, in step
   *  with the formation's hold on it, which is right: it is what makes the
   *  gathering look like gathering. But a particle half way to the surface is
   *  not on a smaller version of the surface, it is somewhere in the middle of
   *  the shape — so a shell built that way lands *inside* the formation and
   *  fills it in, which is the exact opposite of its job. Over a hold of about
   *  a third they go all the way, and the outline is a line.
   *
   *  It is paired with the floor below: they are also not recruited at all
   *  until the hold is real, so what commits fully is only ever material the
   *  formation properly has. */
  crustGrip: 2.6,
  crustJoin: 0.3,
  /** How far the shell's places are crowded toward the crest.
   *
   *  Places across the base are spread evenly, which is right for the material
   *  filling a formation and wrong for the material outlining one: the steeper
   *  the flank, the more outline there is per unit of base, so an even spread
   *  leaves the sharp part of a sharp summit with the fewest particles on it —
   *  exactly where the shape is decided. Above one, they crowd inward. */
  crustCrowd: 1.15,
  /** How reluctant a particle is to be recruited at all.
   *
   *  A formation draws its material off a long stretch of arc and packs it into
   *  a short one, and if everything within reach goes, what is left where it
   *  came from is a hole — a mountain standing over a gap in its own circle,
   *  which is the one thing it must not do. A fixed share held back does not
   *  fix it either: that thins the whole catchment evenly, so the gap becomes a
   *  wide pale band with a hard outer edge where the catchment stops.
   *
   *  So every particle carries a threshold of its own, spread over this range,
   *  and joins only where the formation's hold on the band exceeds it. Under
   *  the summit the hold is total and everything goes; further out fewer and
   *  fewer do, and the band thins by degrees back to its own density with no
   *  edge anywhere. Which is also the density gradient the reference has —
   *  packed hard against the peak, loosening away from it. */
  recruitVary: 0.85,
  /** How much heavier the ink is where a formation has hold of the band.
   *
   *  Not a cheat and not opacity standing in for material: the particles really
   *  are packed several times closer together there than they were lying in the
   *  band, and at a size under a pixel that packing has nowhere to show. This
   *  is what it would have looked like if each of them covered the area it now
   *  has to itself. Scaled by the hold, so it arrives with the mountain and
   *  leaves with it. */
  peakInk: 2.3,
  /** …and what the material *inside* it keeps.
   *
   *  Under one, and that is the whole difference between an ink blot and a
   *  blob. The reference's formations are not solid: every one of them is a
   *  dark, definite outline around a pale stippled interior, and the eye reads
   *  the outline as the object. Filling them in evenly throws that away — it
   *  gives back the grey smudge the outline was there to replace. */
  bodyInk: 1.2,
  /** The share that never lands on the formation at all: carried up past the
   *  crown and left drifting above and around it, faint and slow, so the peak
   *  has an atmosphere rather than an edge against the page. */
  moteShare: 0.05,
  /** How low in the formation a mote may hang, as a share of the summit. Above
   *  the shoulders, so the dust reads as belonging to the peak. */
  moteFloor: 0.45,
  /** How far it rises above that, in px, over the course of its own slow cycle
   *  — and how wide it wanders, as a multiple of the base it came from. */
  moteLift: 22,
  moteWander: 1.5,
  moteRate: 0.33,
  /** What a mote keeps of its alpha. Dust is what you nearly cannot see. */
  moteFade: 0.5,
  /** Texture redraw rate (Hz). Only the smoke and the dust move; the dot band
   *  is pre-rendered once per layout and blitted.
   *
   *  It used to need thirty. Fine particles at fifteen visibly jump from one
   *  position to the next, and there was nothing between the frames to cover
   *  it. The wake is: every particle is still on screen where it was a moment
   *  ago, fading, so the eye reads a path rather than a series of positions —
   *  and twenty is enough again. */
  textureFps: 20,
  /** Hard safety bound on the texture. Sits far enough out that the halo's
   *  own falloff has already reached zero, so this never cuts anything
   *  visible — it only guarantees the layer can't escape the container. */
  /* Per breakpoint, because the two ends of it are decided by different
     things. Desktop wants as much of it as the container will bear, so the
     pillar has somewhere to be taller than the ambient spikes. Mobile cannot
     pay for it: the radius sets the width of the text block inside the ring,
     and taking five percent off it there truncated two lines of the copy. So
     mobile keeps the radius it had and the shorter reach that goes with it. */
  textureClipAllowance: { desktop: 0.31, mobile: 0.26 },
  /** What fraction of the room out to that bound the falloff to zero is spread
   *  over. Small: a formation's summit is the part of it furthest out, so a
   *  falloff spread over the last third takes the tops off everything tall —
   *  which is what a mountain must not have happen to it. It used to be all of it, which meant a spike had lost four fifths of
   *  itself by the time it was two thirds of the way up — the throw was the
   *  right length and what you could see of it was half that. Held to the last
   *  third instead, a spike is at full strength for most of its height and
   *  spends the rest dissolving, which is both what it should look like and
   *  what still guarantees nothing meets the bound with anything left. */
  textureHold: 0.16,

  /* ── The pillar ───────────────────────────────────────────────────────────
     The column that rises at the marker you are pointing at — the anomaly
     breaking through the trace.

     It used to be forty-six SVG lines: a fan of hairline strokes with a
     gaussian envelope, drawn over the ring. Against a ring that was itself a
     drawn line that was consistent. Against a ring made of particles it is the
     one thing left in the picture that is obviously ruled, and it reads as a
     leftover, which is what it was.

     So it is particles too, on the same layer and out of the same machinery as
     the ambient spikes — the difference is only that it is denser, taller, and
     stands where you are pointing rather than where the field says. The
     ambient spikes were pulled back to under three quarters of the room
     available so that this one has somewhere to be taller than. */
  /** Ring radius as a fraction of the container's shorter side. Leaves room
   *  for the spikes to erupt outward without ever reaching the container. */
  radiusRatio: { desktop: 0.375, mobile: 0.395 },

  /** Signal-ring pulse: how long one cycle takes, and the stagger between
   *  markers so they never fire in unison. Ambient/idle motion, not a hover
   *  reaction, so it keeps its own slower pace. */
  pulseSeconds: 2.8,
  pulseStagger: 0.3,
  /** Cursor parallax travel in px near an active marker (desktop only). */
  parallax: 8,
  /** Entrance sequence timings in ms (page load only). */
  entrance: { ring: 150, grain: 750, markers: 1150, text: 550 },
  /** Hover/focus reaction speed: line draw, marker emphasis, text swap.
   *  Kept fast and consistent so the ring feels responsive, never sluggish. */
  hoverMs: 220,
  hoverEasing: 'cubic-bezier(0.22, 1, 0.36, 1)',
  /** How far outside the ring's nominal radius decorative elements (ticks,
   *  pulses, grain) are allowed to sit before being hard-clipped. Keeps the
   *  circle a strict boundary without slicing through the ring's own edge
   *  decorations. */
  clipAllowance: 0.05,
  /** Content block width as a fraction of the ring radius. */
  contentBoxRatio: 0.98,
  /** Floor on that width, in px, so a small ring still gets a readable line
   *  length. Capped by the circle's own chord, so it can never overflow. */
  contentBoxMin: 196,
  /** Fraction of the radius kept clear between the content block's corners
   *  and the ring. Without it the top metadata lines crowd the arc. */
  /* Per breakpoint. The inset buys clear air between the block's corners and
     the arc, and on a wide ring that is cheap — the box is far smaller than
     the circle and the corners are nowhere near it. On a phone it is the most
     expensive number here: the block is nearly as tall as the circle's own
     diameter, so its corners sit close to the top and bottom of the arc, and
     every pixel of inset comes straight off the width. At a sixth of the
     radius it was leaving a hundred-pixel column for body copy. */
  contentEdgeInset: { desktop: 0.16, mobile: 0.06 },
} as const;

/** Clamp a size to a [min, max] range — the JS equivalent of CSS clamp(),
 *  used throughout so nothing is ever a bare fixed pixel value. */
const clampPx = (min: number, value: number, max: number) => Math.min(max, Math.max(min, value));

/** Deterministic PRNG, so the texture is identical on every render and every
 *  reload — the band must not shimmer or reshuffle when React re-renders. */
function seededRandom(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

// ─── Service Data ─────────────────────────────────────────────────────────────
export type ServiceAnomaly = {
  id: string;
  title: string;
  shortDescription: string;
  businessOutcome: string;
  angle: number;
  peakHeight: number;
  peakWidth: number;
  intensity: number;
  slug: string;
  systemCode: string;
};

const SERVICES: ServiceAnomaly[] = [
  {
    id: 'business-strategy',
    title: 'Business Strategy',
    shortDescription:
      'Define clear direction, competitive positioning, and measurable growth objectives.',
    businessOutcome: 'Build a resilient, scalable business framework.',
    angle: -Math.PI / 2,
    peakHeight: 72,
    peakWidth: 0.18,
    intensity: 0.92,
    slug: '/services/business-strategy',
    systemCode: 'SVC-01',
  },
  {
    id: 'brand-positioning',
    title: 'Brand Positioning',
    shortDescription: 'Craft a distinct market identity that resonates with your ideal customers.',
    businessOutcome: 'Establish authority and recognition in your sector.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 1) / 8,
    peakHeight: 58,
    peakWidth: 0.15,
    intensity: 0.78,
    slug: '/services/brand-positioning',
    systemCode: 'SVC-02',
  },
  {
    id: 'market-research',
    title: 'Market Research',
    shortDescription: 'Uncover demand signals, competitor gaps, and untapped customer segments.',
    businessOutcome: 'Make decisions backed by verified market intelligence.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 2) / 8,
    peakHeight: 64,
    peakWidth: 0.16,
    intensity: 0.85,
    slug: '/services/market-research',
    systemCode: 'SVC-03',
  },
  {
    id: 'growth-marketing',
    title: 'Growth Marketing',
    shortDescription: 'Build a repeatable customer acquisition system across every channel.',
    businessOutcome: 'Improve acquisition strategy, conversion, and ROI.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 3) / 8,
    peakHeight: 80,
    peakWidth: 0.2,
    intensity: 1.0,
    slug: '/services/growth-marketing',
    systemCode: 'SVC-04',
  },
  {
    id: 'lead-generation',
    title: 'Lead Generation',
    shortDescription:
      'Design high-converting funnels that attract and qualify your best prospects.',
    businessOutcome: 'Fill your pipeline with sales-ready opportunities.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 4) / 8,
    peakHeight: 66,
    peakWidth: 0.17,
    intensity: 0.88,
    slug: '/services/lead-generation',
    systemCode: 'SVC-05',
  },
  {
    id: 'digital-marketing',
    title: 'Digital Marketing',
    shortDescription: 'Execute integrated campaigns across search, social, and paid channels.',
    businessOutcome: 'Maximize reach, engagement, and measurable return.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 5) / 8,
    peakHeight: 60,
    peakWidth: 0.16,
    intensity: 0.82,
    slug: '/services/digital-marketing',
    systemCode: 'SVC-06',
  },
  {
    id: 'customer-experience',
    title: 'Customer Experience',
    shortDescription:
      'Map and optimize every touchpoint to reduce churn and increase lifetime value.',
    businessOutcome: 'Turn customers into loyal advocates.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 6) / 8,
    peakHeight: 54,
    peakWidth: 0.14,
    intensity: 0.74,
    slug: '/services/customer-experience',
    systemCode: 'SVC-07',
  },
  {
    id: 'business-operations',
    title: 'Business Operations',
    shortDescription:
      'Streamline processes, remove bottlenecks, and build scalable operational systems.',
    businessOutcome: 'Operate efficiently at every stage of growth.',
    angle: -Math.PI / 2 + (Math.PI * 2 * 7) / 8,
    peakHeight: 56,
    peakWidth: 0.15,
    intensity: 0.76,
    slug: '/services/business-operations',
    systemCode: 'SVC-08',
  },
];

type Phase = 'idle' | 'ring' | 'grain' | 'markers' | 'text';
const PHASE_ORDER: Phase[] = ['idle', 'ring', 'grain', 'markers', 'text'];
const reached = (phase: Phase, target: Phase) =>
  PHASE_ORDER.indexOf(phase) >= PHASE_ORDER.indexOf(target);

interface DivergenceTimelineProps {
  services?: ServiceAnomaly[];
  interactive?: boolean;
  animated?: boolean;
  /** How agitated the ring is, 0 to 1.
   *
   *  Zero is the ring barely moving: the wave is a breath, the band is nearly a
   *  circle, and anomalies never arrive. One is the wave running hard, the
   *  band distorting, and a ripple leaving the circumference every second or
   *  two. It scales the amplitudes and the rates together rather than either on
   *  its own, because a big slow wave and a small fast one both read as wrong —
   *  what changes with agitation is how much is happening, and everything that
   *  is happening speeds up with it.
   *
   *  Read every frame from a ref, so it can be driven by anything — a scroll
   *  position, a live figure, a state machine — without rebuilding the field. */
  intensity?: number;
}

export default function DivergenceTimeline({
  services = SERVICES,
  interactive = true,
  animated = true,
  intensity = TUNING.intensity,
}: DivergenceTimelineProps) {
  /* The ring navigates in JavaScript rather than through a link, so the
     language has to be carried by hand: a bare `/services/x` from a Lithuanian
     page lands on the English one. */
  const { lang } = useTranslation();
  const go = useCallback(
    (slug: string) => {
      window.location.href = pathForLang(slug, lang);
    },
    [lang]
  );

  const clipUid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const clipId = `dt-clip-${clipUid}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hazeRef = useRef<HTMLCanvasElement>(null);
  const dustRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  /** Accumulated halo-drift time, so pausing off screen and resuming does
   *  not snap the smoke to a new phase. */
  const clockRef = useRef(0);
  /** Where the pillar stands and how far it has risen. A ref, not state: the
   *  texture effect builds and sizes three canvases when it runs, and pointing
   *  at a marker must not be a reason to do any of that again. */
  const pillarRef = useRef<{
    angle: number;
    level: number;
    target: number;
    /** Where it should stand next, once it has finished going down. */
    pending: number | null;
  }>({ angle: 0, level: 0, target: 0, pending: null });
  /** Where the cursor's influence is, in the container's own coordinates, and
   *  whether there is one at all. It chases the pointer rather than tracking it
   *  exactly — see cursorEase. A ref, like the pillar's: the field reads it
   *  every frame and React must never hear about it. */
  const cursorRef = useRef({ x: 0, y: 0, tx: 0, ty: 0, on: false });
  /* How agitated the ring is, in a ref rather than in the effect's deps. Put in
     the deps it would rebuild the whole field on every change, which is a
     visible reset; read from a ref the loop simply uses the new value on its
     next frame and the wave grows or settles into it. */
  const intensityRef = useRef(intensity);
  intensityRef.current = Math.min(1, Math.max(0, intensity));

  const [size, setSize] = useState({ w: 0, h: 0 });
  const [panelSize, setPanelSize] = useState({ w: 0, h: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isOnScreen, setIsOnScreen] = useState(true);
  const [phase, setPhase] = useState<Phase>('idle');
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const [displayId, setDisplayId] = useState(services[0]?.id ?? '');
  const [textOut, setTextOut] = useState(false);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [introBurstId, setIntroBurstId] = useState<string | null>(null);
  /** Bumped when the OS colour scheme changes, to force a canvas repaint. */
  const [schemeKey, setSchemeKey] = useState(0);

  const activeIdRef = useRef(services[0]?.id ?? '');
  const activeMarkerRef = useRef<string | null>(null);
  const autoRotateRef = useRef(true);
  const autoRotateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastPointerTypeRef = useRef<string>('mouse');
  /** Snapshot of the active marker taken *before* the current tap, so the
   *  click handler can tell "this tap just activated it" from "it was
   *  already active" even though React may have already applied the state
   *  update by the time click fires (pointerdown → pointerup → click all
   *  land in the same gesture, but not the same render). */
  const preTapActiveIdRef = useRef<string | null>(null);
  const markerButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // ── Reduced motion ────────────────────────────────────────────────────────
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setPrefersReducedMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // ── Colour scheme ─────────────────────────────────────────────────────────
  // The SVG layers pick up the theme for free through var(--m-ring-rgb), but
  // canvas paints with literal colour strings, so it has to be told to repaint.
  //
  // Two independent triggers, and both are needed. The media query covers the
  // OS or browser flipping. The attribute observer covers the theme control,
  // which sets data-theme on <html> — that fires no media query event, so
  // watching only matchMedia left the canvas painted in the previous theme's
  // colours until some unrelated event happened to re-run the effect.
  useEffect(() => {
    const bump = () => setSchemeKey((n) => n + 1);

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', bump);

    const observer = new MutationObserver(bump);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      mq.removeEventListener('change', bump);
      observer.disconnect();
    };
  }, []);

  // ── Measure the container, not the window ─────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      setSize({ w, h });
      setIsMobile((w || window.innerWidth) < MOBILE_BREAKPOINT);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Pause the wave loop while the circle is scrolled off-screen ───────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => setIsOnScreen(entry.isIntersecting), {
      threshold: 0.01,
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const layout = useMemo(
    () =>
      getCircleLayout(size.w, size.h, {
        radiusRatio: isMobile ? TUNING.radiusRatio.mobile : TUNING.radiusRatio.desktop,
      }),
    [size.w, size.h, isMobile]
  );

  const hitRadius = useMemo(
    () => (layout ? topicHitRadius(layout, services.length) : 0),
    [layout, services.length]
  );

  const hasLayout = Boolean(layout);

  // ── Page-load sequence: ring → grain → markers → text ─────────────────────
  // Only pauses the section's own reveal; the hero block above animates on
  // its own timeline before this one starts.
  useEffect(() => {
    if (!hasLayout) return;
    if (prefersReducedMotion) {
      setPhase('text');
      return;
    }
    const timers = [
      setTimeout(() => setPhase('ring'), TUNING.entrance.ring),
      setTimeout(() => setPhase('grain'), TUNING.entrance.grain),
      setTimeout(() => setPhase('markers'), TUNING.entrance.markers),
      setTimeout(() => setPhase('text'), TUNING.entrance.markers + TUNING.entrance.text),
      // One marker gets a stronger initial pulse, to hint the ring is
      // interactive before anyone has touched it.
      setTimeout(
        () => setIntroBurstId(services[0]?.id ?? null),
        TUNING.entrance.markers + TUNING.entrance.text + 500
      ),
      setTimeout(
        () => setIntroBurstId(null),
        TUNING.entrance.markers + TUNING.entrance.text + 2200
      ),
    ];
    return () => timers.forEach(clearTimeout);
  }, [hasLayout, prefersReducedMotion, services]);

  // ── Measure the readout so connectors start at its real edge. The panel's
  //    own box is now a fixed size for a given radius (see contentMetrics
  //    below), so this only re-fires on resize, never on a topic switch. ────
  useEffect(() => {
    const el = panelRef.current;
    if (!el) {
      setPanelSize({ w: 0, h: 0 });
      return;
    }
    const measure = () => setPanelSize({ w: el.offsetWidth, h: el.offsetHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile, size.w, size.h]);

  // ── Stable noise table backing the whole texture ──────────────────────────
  const noiseTable = useMemo(() => {
    const rand = seededRandom(TUNING.seed * 6151 + 42);
    return Array.from({ length: 1024 }, () => rand());
  }, []);
  const noiseAt = useCallback(
    (i: number, offset = 0) => noiseTable[((i | 0) + offset) & 1023],
    [noiseTable]
  );

  // ── Canvas texture: the dense perimeter band + drifting smoke halo ────────
  // Thousands of dots is well past what SVG can carry, so the texture lives
  // on a canvas. Critically, the context is scaled by devicePixelRatio ONCE
  // and everything is then drawn in CSS pixels off the same `layout` the SVG
  // and the DOM overlays use — mixing device pixels into shared coordinates
  // is exactly what put the old build's connector and panel in the wrong
  // place on high-DPI screens.
  useEffect(() => {
    const canvas = canvasRef.current;
    const haze = hazeRef.current;
    const dust = dustRef.current;
    if (!canvas || !haze || !dust || !layout || size.w <= 0 || size.h <= 0) return;
    const ctx = canvas.getContext('2d');
    const hctx = haze.getContext('2d');
    const uctx = dust.getContext('2d');
    if (!ctx || !hctx || !uctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(size.w * dpr);
    canvas.height = Math.round(size.h * dpr);
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;

    /* The halo gets a canvas of its own, and a small one: a fraction of the
       display size, stretched back up by CSS. The browser's own filtering does
       the upscale during compositing, which is both the blur that turns the
       particles into smoke and the reason none of it costs anything — there is
       no full-size blit in JavaScript at all.

       Splitting it from the dot band matters as much. The band never changes
       between layouts, and it was being cleared and blitted at full resolution
       on every animated frame purely because it shared a canvas with the part
       that moves. Now it is painted once and left alone. */
    const { cx, cy, radius } = layout;
    const clipR = radius * (1 + TUNING.textureClipAllowance[isMobile ? 'mobile' : 'desktop']);

    /* The two animated layers cover the ring's own square and nothing else.
       Everything they touch is inside the safety circle by construction, and
       the container is a third again as wide as that circle is across — so
       giving them the whole container meant a third of every clear, every
       fade and every upload to the GPU was spent on ground that can never
       have anything drawn on it. */
    const boxSide = Math.ceil(clipR * 2);
    const boxX = Math.round(cx - clipR);
    const boxY = Math.round(cy - clipR);
    const fitBox = (el: HTMLCanvasElement, scale: number) => {
      el.width = Math.max(1, Math.round(boxSide * scale));
      el.height = Math.max(1, Math.round(boxSide * scale));
      el.style.width = `${boxSide}px`;
      el.style.height = `${boxSide}px`;
      el.style.left = `${boxX}px`;
      el.style.top = `${boxY}px`;
    };
    const smokeScale = TUNING.smokeScale;
    const liveScale = Math.min(dpr, TUNING.liveDprCap);
    fitBox(haze, smokeScale);
    fitBox(dust, liveScale);

    // Sample the same custom properties the SVG uses, so the canvas texture
    // tracks the theme instead of being pinned to near-black.
    //
    // These can come back empty on the very first paint, before the stylesheet
    // has been applied — which does not happen on a fast desktop load but does
    // on a phone. A fixed light-mode fallback then painted a near-black halo
    // onto a dark page, i.e. an invisible one, and it stayed that way until
    // some later event re-ran this effect. So the fallback is derived from the
    // scheme actually in force rather than assumed.
    const root = document.documentElement;
    const explicit = root.getAttribute('data-theme');
    const isDark =
      explicit === 'dark' ||
      (explicit !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const css = getComputedStyle(canvas);
    const grainRgb =
      css.getPropertyValue('--m-ring-grain-rgb').trim() ||
      (isDark ? '210, 210, 206' : '30, 30, 30');
    const ringRgb =
      css.getPropertyValue('--m-ring-rgb').trim() || (isDark ? '226, 226, 222' : '20, 20, 20');

    // The dot band never changes until the layout does, so it is painted here
    // and never touched again.
    const dotCount = isMobile ? TUNING.ringDotCount.mobile : TUNING.ringDotCount.desktop;
    /* Four passes on a phone against six. The rim's darkness is overlap, so
       cutting passes thins it — and now that a grain is a third of the size it
       was, three passes over a shorter circumference read as a sketch of the
       rim rather than the rim. The count goes up with it: what matters is
       stamps per pixel of circumference, and a phone's is a third shorter. */
    const dotLayers = isMobile ? 3 : TUNING.ringDotLayers;
    /* How thick the rim is at a given angle, in eighths of a turn resolved to a
       small table — it is three sines and it is asked for tens of thousands of
       times, so it is worked out 256 times instead. */
    const RIM = 256;
    const rim = new Float32Array(RIM);
    for (let i = 0; i < RIM; i++) {
      let k = 1;
      for (const w of TUNING.ringWaves) {
        k += w.amplitude * Math.sin((i / RIM) * TAU * w.frequency + w.phase);
      }
      rim[i] = Math.max(0.35, k);
    }

    /* The rim is painted in slices across several frames rather than in one go.

       It is tens of thousands of fills, and one fill is cheap but that many in
       a single task is not: measured on a 4x-throttled phone, the longest task
       after load went from 300ms to 690ms when the grain got finer and the
       count went up to carry the same weight. That is a visible stall on a
       phone, twice over, since it is painted again on the settle frame.

       Nothing about it has to happen at once. The layer fades in over more than
       a second, so a rim that assembles over a dozen frames arrives before the
       fade does and no one sees it building. Slicing by a fixed number of
       stamps rather than by pass keeps each slice the same size whatever the
       count or the number of passes is. */
    const STAMPS_PER_FRAME = 2200;
    const totalStamps = dotLayers * dotCount;
    let bandRaf = 0;

    const paintSlice = (from: number) => {
      const dctx = ctx;
      dctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (from === 0) dctx.clearRect(0, 0, size.w, size.h);
      dctx.save();
      dctx.beginPath();
      dctx.arc(cx, cy, clipR, 0, TAU);
      dctx.clip();

      const half = TUNING.ringDotScatter * 0.5;
      const step = TAU / dotCount;
      const end = Math.min(totalStamps, from + STAMPS_PER_FRAME);
      /* One fill colour per pass rather than per particle. The alpha of any one
         of them is not what makes the rim dark — the overlap is — and setting a
         style means building and parsing a colour string, which at this count
         was most of the time this function spent. */
      let pass = -1;
      for (let k = from; k < end; k++) {
        const l = (k / dotCount) | 0;
        if (l !== pass) {
          pass = l;
          dctx.fillStyle = `rgba(${grainRgb},${(TUNING.ringDotAlpha * (1 - l * 0.11)).toFixed(3)})`;
        }
        const i = k - l * dotCount;
        // One noise slot per particle per pass — sharing a slot between
        // neighbours lands them on the same offset, which reads as combing.
        const ni = i * 3 + l * 419;
        const angle = i * step + (noiseAt(ni, 96) - 0.5) * step * 1.6;
        /* Cubed, so the scatter crowds the line. An even spread across the band
           would give a grey smear of uniform density; this gives a core that
           fills in solid and edges that fray.

           Mostly the cube, with a quarter of the straight line left in: the cube
           alone piles so much of the scatter onto the exact radius that the core
           comes out a hairline — which reads as a drawn stroke with grain around
           it, the very thing this replaced. */
        const u = noiseAt(ni, l * 32) * 2 - 1;
        const bias = u * u * u * 0.74 + u * 0.26;
        const thick = half * rim[((angle * (RIM / TAU)) | 0) & (RIM - 1)];
        const r = radius + (bias < 0 ? bias * TUNING.ringDotInner : bias) * thick;
        const d = TUNING.ringDotSize + noiseAt(ni, l * 64 + 16) * TUNING.ringDotSizeRange;
        // fillRect, not arc+fill: at this size the two are the same picture and
        // one of them is a path, a rasterisation and a fill each time.
        dctx.fillRect(cx + Math.cos(angle) * r - d, cy + Math.sin(angle) * r - d, d * 2, d * 2);
      }
      dctx.restore();
      return end;
    };

    const paintBand = () => {
      cancelAnimationFrame(bandRaf);
      let cursor = paintSlice(0);
      const more = () => {
        if (cursor >= totalStamps) return;
        cursor = paintSlice(cursor);
        bandRaf = requestAnimationFrame(more);
      };
      if (cursor < totalStamps) bandRaf = requestAnimationFrame(more);
    };
    paintBand();

    /* One sprite, stamped many times over — building a fresh radial gradient
       per blob per frame is the expensive way to do this.

       It falls off as a gaussian rather than along the straight line the halo
       used to have. A linear ramp keeps a definite shoulder where it meets
       zero, and a few hundred shoulders is what a field of discs looks like; a
       gaussian has no edge to find, so what overlaps is smoke.

       The dust used to have a sprite of its own, wound tight with a flat core
       so it would survive being scaled down to two pixels. It does not need one
       any more: it is filled rectangles now, which at that size is the same
       shape with a harder edge and no source texture to sample. */
    const makeSprite = (px: number, spread: number) => {
      const c = document.createElement('canvas');
      c.width = px;
      c.height = px;
      const g2 = c.getContext('2d');
      if (!g2) return c;
      const g = g2.createRadialGradient(px / 2, px / 2, 0, px / 2, px / 2, px / 2);
      for (let stop = 0; stop <= 10; stop++) {
        const q = stop / 10;
        g.addColorStop(q, `rgba(${ringRgb},${Math.exp(-(q * q) / spread).toFixed(4)})`);
      }
      g.addColorStop(1, `rgba(${ringRgb},0)`);
      g2.fillStyle = g;
      g2.fillRect(0, 0, px, px);
      return c;
    };
    const sprite = makeSprite(64, 0.42);

    const smokeCount = isMobile ? TUNING.smokeCount.mobile : TUNING.smokeCount.desktop;
    /* ── The population ───────────────────────────────────────────────────
       Positions and velocities that persist between frames, plus the fixed
       properties a particle keeps for its whole life: where it rests on the
       ring, how big it is, how far it rides when the field lifts, and which
       slot in the column it takes when it is gathered.

       Flat typed arrays rather than objects. Five thousand objects would be
       five thousand allocations to chase every frame; five arrays is five. */
    const liveCount = isMobile ? TUNING.liveCount.mobile : TUNING.liveCount.desktop;
    const pxs = new Float32Array(liveCount);
    const pys = new Float32Array(liveCount);
    const vxs = new Float32Array(liveCount);
    const vys = new Float32Array(liveCount);
    /** Where it rests: an angle on the ring, and a signed offset from the line. */
    const homeA = new Float32Array(liveCount);
    const homeR = new Float32Array(liveCount);
    const sizes = new Float32Array(liveCount);
    const alphas = new Float32Array(liveCount);
    /** How high this one stands in whatever peak it finds itself in.
     *
     *  Weighted toward the base, but not as far as an even area density would
     *  put it. That would be the right answer with unlimited particles and is
     *  the wrong one here: it leaves about one particle in a hundred near a
     *  summit, and a summit drawn by one particle is not there. What the eye
     *  reads first is the outline, so the outline gets enough to be an
     *  outline and the base carries a little less than its share. */
    const climbs = new Float32Array(liveCount);
    /** Where across the mountain's base this one stands, -1 to 1. It doubles
     *  as this particle's share of the crumbling on an ambient slope, where
     *  there is no mountain to have a place in. */
    const slotU = new Float32Array(liveCount);
    /** …and the other axis of that, so the straying is not all radial. */
    const frays = new Float32Array(liveCount);
    /** What a particle does when it is gathered: 0 lies in the body of the
     *  formation, 1 is laid on its surface, 2 drifts as dust above the crown.
     *  Fixed per particle, so the same ones do the same job every time and
     *  neither the outline nor the atmosphere reshuffles frame to frame. */
    const roles = new Uint8Array(liveCount);
    const ROLE_CRUST = 1;
    const ROLE_MOTE = 2;
    /** What this particle's ink is multiplied by once a formation has all of
     *  it — heavier on the outline, lighter inside, lighter still in the air.
     *  Worked out once at setup, so the drawing pass reads one number instead
     *  of asking what job the particle is doing. */
    const inks = new Float32Array(liveCount);
    /** How firm a hold a formation needs on this particle before it moves at
     *  all — its own, fixed, so the band always thins in the same places. */
    const joins = new Float32Array(liveCount);

    /* ── The buffer ───────────────────────────────────────────────────────
       The live layer is written pixel by pixel and handed over in one go,
       rather than drawn with thousands of calls into the canvas.

       That is not a micro-optimisation, it is the difference between this
       being possible and not. A fillRect at a fractional position and a
       fractional size cannot take the fast path — it is an antialiased path
       fill — and measured on a 4x-throttled phone each one cost about eight
       microseconds. Five thousand of them is forty milliseconds a frame, which
       pushed every frame past the twenty-a-second budget, which made the field
       run on every frame instead of every third, which cost forty more. The
       same five thousand particles with the drawing taken out ran faster than
       the build before them: the physics is free and the canvas calls were all
       of it.

       Written straight into the pixels there is no per-particle call at all. A
       particle deposits its ink across the four pixels it falls between, which
       is four reads and four writes, and the whole buffer goes to the canvas
       once. */
    const bufW = dust.width;
    const bufH = dust.height;
    const frame = uctx.createImageData(bufW, bufH);
    const pixels = new Uint32Array(frame.data.buffer);
    // A pixel's bytes are R,G,B,A in memory; which end of a word that is
    // depends on the machine, so it is asked rather than assumed.
    const littleEndian = (() => {
      const probe = new Uint32Array(1);
      probe[0] = 1;
      return new Uint8Array(probe.buffer)[0] === 1;
    })();
    const inkParts = ringRgb
      .split(',')
      .map((v) => Math.min(255, Math.max(0, parseInt(v, 10) || 0)));
    const inkR = inkParts[0] ?? 0;
    const inkG = inkParts[1] ?? inkR;
    const inkB = inkParts[2] ?? inkR;
    /** The particle's colour with an empty alpha byte, ready to be or-ed. */
    const inkBase = littleEndian
      ? (inkB << 16) | (inkG << 8) | inkR
      : (inkR << 24) | (inkG << 16) | (inkB << 8);
    const alphaShift = littleEndian ? 24 : 0;

    /** Add one particle's share of ink to one pixel, saturating at opaque. */
    const splat = (at: number, ink: number) => {
      if (ink < 1) return;
      const was = (pixels[at] >>> alphaShift) & 255;
      const now = was + ink;
      pixels[at] = (((now > 255 ? 255 : now) | 0) << alphaShift) | inkBase;
    };

    for (let i = 0; i < liveCount; i++) {
      const n = i * 5;
      homeA[i] = (i / liveCount) * TAU + (noiseAt(n, 0) - 0.5) * (TAU / liveCount) * 2.4;
      /* Cubed, so the cloud crowds the line and thins outward rather than
         sitting in an even ring. Most of it lies outside — the inside of the
         circle is where the reading is. */
      const u = noiseAt(n, 16);
      const depth = u * u * u * TUNING.liveSpread;
      homeR[i] =
        noiseAt(n, 32) < TUNING.liveInward
          ? -TUNING.liveInner - depth * 0.55
          : TUNING.liveInner + depth;
      sizes[i] = TUNING.liveSize + noiseAt(n, 48) * TUNING.liveSizeRange;
      alphas[i] = 0.45 + noiseAt(n, 64) * 0.55;
      climbs[i] = 1 - Math.pow(1 - noiseAt(n, 80), 0.7);
      slotU[i] = noiseAt(n, 96) * 2 - 1;
      frays[i] = noiseAt(n, 112) * 2 - 1;
      const role = noiseAt(n, 128);
      roles[i] =
        role < TUNING.moteShare
          ? ROLE_MOTE
          : role < TUNING.moteShare + TUNING.crustShare
            ? ROLE_CRUST
            : 0;
      joins[i] =
        roles[i] === ROLE_CRUST
          ? TUNING.crustJoin + noiseAt(n, 144) * (TUNING.recruitVary - TUNING.crustJoin)
          : noiseAt(n, 144) * TUNING.recruitVary;
      inks[i] =
        roles[i] === ROLE_MOTE
          ? TUNING.moteFade
          : roles[i] === ROLE_CRUST
            ? TUNING.peakInk
            : TUNING.bodyInk;
      if (roles[i] === ROLE_CRUST) {
        const u = slotU[i];
        const au = u < 0 ? -u : u;
        slotU[i] = (u < 0 ? -1 : 1) * Math.pow(au, TUNING.crustCrowd);
      }
      // Start where it belongs, so the first frame is already settled.
      const r0 = radius + homeR[i];
      pxs[i] = cx + Math.cos(homeA[i]) * r0;
      pys[i] = cy + Math.sin(homeA[i]) * r0;
    }

    /** Outward room between the ring line and the safety clip, and how much of
     *  it the falloff to zero is spread over — see textureHold. */
    const maxOut = Math.max(1, clipR - radius);
    const hold = TUNING.textureHold;

    /* ── The angular field ──────────────────────────────────────────────────
       Three things vary around the circumference and over time: how far the
       halo swells outward, where ridges of dust are standing, and how dense
       the smoke is. All three are functions of angle and time alone, so they
       are worked out into a table once a frame and read back per particle for
       two loads and a lerp — where evaluating them in place would have been a
       dozen sines each, a thousand times over.

       The table is a power of two long, which is what makes the wrap a mask.
       That matters: particles flow continuously around the ring and their
       angle grows without bound. */
    const FIELD = 512;
    const FIELD_MASK = FIELD - 1;
    const FIELD_K = FIELD / TAU;
    /* Eight numbers per angle, not four.
   
       A formation is not a height. It is a summit height, a direction and
       distance to that summit, a base width, and a catchment — how strongly
       the band around it is being drawn in. Four of those, because a mountain's
       base and the stretch of ground it was piled out of are different things,
       and the first three attempts at this all failed on that one point: a
       formation that can only use the particles already resting inside its own
       base has about half a particle per pixel to build itself from, which is
       a whisper however tall you make it. */
    const FIELD_STRIDE = 8;
    const F_SWELL = 0;
    const F_SUMMIT = 1;
    const F_DENSITY = 2;
    const F_TO_CREST = 3;
    const F_BASE = 4;
    const F_GRAB = 5;
    const F_ID = 6;
    const F_CLAIM = 7;
    const field = new Float32Array(FIELD * FIELD_STRIDE);
    /* Per formation, the things a particle needs to know to work out the shape
       of the slope it is standing on: how each flank falls away, and where its
       notches are. Kept beside the field rather than in it because they belong
       to the formation, not to an angle — and a particle reads them at its own
       place across the base, which is not the angle it came from. */
    /* Per formation: the erosion's phase and depth, how sharply its lobes come
       to a point, how many of them there are, and then three numbers for each —
       where its centre sits across the base, one over its half-width, and its
       height taken to the inverse of the point exponent. Taken to a power,
       because the profile is the tallest of the lobes and a power is monotonic,
       so all of them can be compared pre-raised and the profile put back
       through the power once at the end.
   
       All four of the shape numbers are per formation rather than global,
       because the perimeter is not made of one kind of thing: a spire, a
       splinter, a crumbling mass and a data spike are the same machinery with
       different values in these four slots. */
    const LOBE = 3;
    const MAX_LOBES = 4;
    const M_PHASE = 0;
    const M_ERODE = 1;
    const M_POINT = 2;
    const M_COUNT = 3;
    const M_LOBES = 4;
    const META = M_LOBES + MAX_LOBES * LOBE;

    /** Fill one formation's shape into the meta table: its erosion, how sharply
     *  its lobes point, and where each lobe sits across its base.
     *
     *  Lobes are what stops a formation being a triangle. Each rises from its
     *  foot to a point with a flank that caves inward on the way, and the
     *  profile is the tallest of them at each place across the base — so where
     *  two overlap the outline pinches into a notch instead of running on as
     *  one slope. One lobe is a single clean peak; four is a crumbling mass.
     */
    const writeShape = (
      m: number,
      kind: { lobes: number; point: number; erode: number },
      seed: number
    ) => {
      const lobes = Math.min(MAX_LOBES, Math.max(1, kind.lobes));
      meta[m + M_PHASE] = noiseAt(seed, 40) * TAU;
      meta[m + M_ERODE] = kind.erode;
      meta[m + M_POINT] = kind.point;
      meta[m + M_COUNT] = lobes;
      const inv = 1 / kind.point;
      for (let l = 0; l < lobes; l++) {
        const nl = seed + l * 37;
        const o = m + M_LOBES + l * LOBE;
        if (l === 0) {
          // The crown, in the middle, at full height — and one to any power is
          // one, so it needs no raising.
          meta[o] = 0;
          meta[o + 1] = 1 / (lobes === 1 ? 1 : TUNING.lobeCrown);
          meta[o + 2] = 1;
          continue;
        }
        /* A flank lobe. The first two take a side each; anything after them is
           placed wherever its own noise puts it, which is what keeps a
           formation from coming out as a mirror of itself. */
        const side = l === 1 ? 1 : l === 2 ? -1 : noiseAt(nl, 8) < 0.5 ? 1 : -1;
        const w = TUNING.lobeWidth + noiseAt(nl, 16) * TUNING.lobeWidthRange;
        const c = TUNING.lobeFlank + noiseAt(nl, 24) * TUNING.lobeFlankRange;
        const h = TUNING.lobeRise + noiseAt(nl, 32) * TUNING.lobeRiseRange;
        meta[o] = side * Math.min(c, 1 - w);
        meta[o + 1] = 1 / w;
        meta[o + 2] = Math.pow(h, inv);
      }
    };

    /**
     * Lay one formation into a field: its summit height across its whole
     * catchment, how strongly it has hold of the band at each angle, how wide
     * its base is, which way its summit lies, and which formation it is.
     *
     * Where two overlap, whichever has the stronger claim takes the band
     * outright. Blending them would make one wide mound out of two; this way a
     * clump comes out as several summits standing together.
     */
    const writeFormation = (
      into: Float32Array,
      ci: number,
      grabHalf: number,
      baseW: number,
      claimW: number,
      height: number,
      lean: number,
      id: number,
      holdScale: number
    ) => {
      /* How much arc it owns is not how wide it is. A needle a pixel across
         owns a pixel of band, which is a dozen particles, which is not a
         needle — it is nothing. So the thin kinds claim a stretch far wider
         than they are and pack all of it into their own narrow base. */
      const baseHalf = Math.max(0.5, claimW * FIELD_K);
      const g0 = Math.ceil(ci - grabHalf);
      const g1 = Math.floor(ci + grabHalf);
      for (let i = g0; i <= g1; i++) {
        const reach = (i - ci) / grabHalf;
        /* Pushed up before it is smoothed, so the inner part of the catchment
           commits to the formation outright and only the outer edge is
           partly-taken. Left as a plain ramp, most of the material a formation
           has to build with never quite arrives — it hangs half way between
           where it was resting and where it is wanted, and what stands there
           is a smear rather than a mountain. */
        const span = (1 - (reach < 0 ? -reach : reach)) * TUNING.spikeGrip;
        const near = span > 1 ? 1 : span;
        // Smoothstepped, so the band is drawn in hard near the middle and
        // barely at the edges, with no line anywhere that the gathering starts.
        const grab = near * near * (3 - 2 * near) * holdScale;
        if (grab <= 0.0001) continue;
        /* Who owns this stretch of band is a different question from who is
           drawing material off it, and answering both with the catchment was
           what flattened a group into one cone: a catchment is many times a
           base wide, so it is a plateau, and on a plateau the tallest summit
           simply wins everywhere and no shoulder is ever seen.

           Ownership is decided within the base instead — near a shoulder's own
           base the shoulder wins even though the principal is taller — with a
           small share of the catchment left in it so that the ground beyond
           every base still belongs to the biggest thing near it. */
        const bu = (i - ci < 0 ? ci - i : i - ci) / baseHalf;
        const k = bu >= 1 ? 0 : 1 - bu;
        const claim = height * (k * k * (3 - 2 * k) + TUNING.spikeReach * grab);
        if (claim <= 0.0001) continue;
        const o = (i & FIELD_MASK) * FIELD_STRIDE;
        if (claim > into[o + F_CLAIM]) {
          into[o + F_CLAIM] = claim;
          into[o + F_SUMMIT] = height;
          into[o + F_BASE] = baseW;
          into[o + F_ID] = id;
          /* How far, and which way, the summit is from here. It is what lets a
             particle climbing be drawn in toward the top as it rises, which is
             the whole difference between a swelling and a peak: a swelling is
             the band moved outward, a peak is the band gathered inward as it
             goes up. The lean is folded in, so what the climbers converge on is
             not the point directly outside the base — that is all a leaning
             summit is. */
          into[o + F_TO_CREST] = (ci - i) / FIELD_K + lean;
        }
        /* The hold on the band is the strongest any formation has here, not
           the owner's. They are the same thing over a summit's own base; out on
           the shared ground between two of them it is the difference between a
           seam and none. */
        if (grab > into[o + F_GRAB]) into[o + F_GRAB] = grab;
      }
    };

    /* ── The perimeter, laid down once ────────────────────────────────────────
       Everything the ring wears at rest is worked out here, at layout, and
       never again: the formations do not move, so there is nothing to
       recompute. Each frame copies this into the live field and then writes
       whatever the pointer has raised on top of it.

       A clock hour is turned into an angle with twelve at the top, running
       clockwise, which is how the perimeter was described and the only sane way
       to talk about where things are on a circle. */
    const hourAngle = (h: number) => -Math.PI / 2 + (h / 12) * TAU;

    const kinds = TUNING.peakKinds as Record<
      string,
      { lobes: number; point: number; erode: number; lean: number; claim: number; wide: number }
    >;
    const meta = new Float32Array(TUNING.massifPeaks * META);
    /** Centre and strength for each anomaly alive this frame. */
    const anomScratch = new Float32Array(TUNING.anomalies * 2);

    /* ── How heavy the line is, arc by arc ───────────────────────────────────
       Two profiles round the circumference, blended between the positions the
       band table names: one scales the rim's own thickness, the other the haze
       around it. They are what make the difference between an arc that is a
       brushstroke and an arc that is a broad smoky sweep — without either, the
       formations sit on a band of unvarying weight and the whole thing reads as
       decoration applied to a circle rather than as the circle itself
       behaving. */
    const zoneBand = new Float32Array(FIELD);
    const zoneHaze = new Float32Array(FIELD);
    {
      const stops = (TUNING.arcWeight as readonly { at: number; band: number; haze: number }[])
        .map((z) => ({ a: (hourAngle(z.at) + TAU * 2) % TAU, band: z.band, haze: z.haze }))
        .sort((x, y) => x.a - y.a);
      for (let i = 0; i < FIELD; i++) {
        const a = (i / FIELD) * TAU;
        let hi = 0;
        while (hi < stops.length && stops[hi]!.a <= a) hi += 1;
        const next = stops[hi % stops.length]!;
        const prev = stops[(hi - 1 + stops.length) % stops.length]!;
        let gap = next.a - prev.a;
        if (gap <= 0) gap += TAU;
        let along = a - prev.a;
        if (along < 0) along += TAU;
        const u = gap > 0 ? along / gap : 0;
        // Smoothstepped between stops, so no zone has an edge.
        const e = u * u * (3 - 2 * u);
        zoneBand[i] = prev.band + (next.band - prev.band) * e;
        zoneHaze[i] = prev.haze + (next.haze - prev.haze) * e;
      }
    }
    /** The same weighting at the rim table's own resolution, for the painted
     *  band, which is laid down in RIM buckets rather than FIELD ones. */
    for (let i = 0; i < RIM; i++) {
      rim[i] *= zoneBand[((i * FIELD) / RIM) | 0];
    }

    const buildField = (t: number) => {
      const swell = TUNING.smokeSwell;
      const dens = TUNING.smokeDensity;
      /* Agitation, as two multipliers around the middle setting: how big the
         wave is, and how fast everything moves. Taken together rather than
         either alone — a big slow wave and a small fast one both read as
         wrong. */
      const level = intensityRef.current;
      const gain = 1 + (level - TUNING.intensity) * TUNING.intensitySwing;
      const rate = 1 + (level - TUNING.intensity) * TUNING.intensityRate;

      /* ── Anomalies ──────────────────────────────────────────────────────
         A handful of bumps, each born at its own place and its own moment,
         travelling a little way round as it fades. Worked out here, once a
         frame, rather than per angle: each is a centre and a strength, and the
         loop below adds whichever of them reach it.

         The lifetimes are staggered by an irrational fraction of each other, so
         two never come round together and the ring never repeats. */
      const anomN = TUNING.anomalies;
      const anomC = anomScratch;
      let anomLive = 0;
      for (let k = 0; k < anomN; k++) {
        const life = TUNING.anomalyLife * (0.7 + noiseAt(k * 71, 0) * 0.6);
        const phase = ((t * rate) / life + noiseAt(k * 71, 8)) % 1;
        // A bump that rises quickly and falls away slowly, and is nothing at
        // either end, so an anomaly never appears or vanishes abruptly.
        const amp = phase * (1 - phase) * 4;
        if (amp < 0.01) continue;
        const born = Math.floor((t * rate) / life + noiseAt(k * 71, 8));
        // Somewhere new each time it comes round.
        const at = noiseAt(k * 71 + born * 13, 16) * TAU + phase * 0.5;
        anomC[anomLive * 2] = at;
        anomC[anomLive * 2 + 1] = amp * amp * level;
        anomLive += 1;
      }

      for (let i = 0; i < FIELD; i++) {
        const a = i / FIELD_K;

        let s = 0;
        for (let k = 0; k < swell.length; k++) {
          const w = swell[k];
          s += w.amplitude * Math.sin(a * w.frequency - t * rate * w.speed * TAU);
        }
        s *= gain;
        for (let k = 0; k < anomLive; k++) {
          let d = a - anomC[k * 2];
          // Wrapped to the short way round, so a bump at angle zero reaches
          // both sides of it rather than only one.
          d -= TAU * Math.round(d / TAU);
          const u = d / TUNING.anomalySpan;
          if (u > 3 || u < -3) continue;
          s += anomC[k * 2 + 1] * Math.exp(-u * u) * (TUNING.anomalyPush / TUNING.smokeSwellPush);
        }

        const o = i * FIELD_STRIDE;
        field[o + F_SWELL] = s;
        /* Cleared, every frame. Nothing stands on the ring at rest — the
           perimeter is a band of particles on a travelling wave and nothing
           else — so there is nothing to carry over. */
        field[o + F_SUMMIT] = 0;
        field[o + F_TO_CREST] = 0;
        field[o + F_BASE] = 0;
        field[o + F_GRAB] = 0;
        field[o + F_ID] = 0;
        field[o + F_CLAIM] = 0;
        field[o + F_DENSITY] =
          dens.base +
          dens.wave *
            (0.5 + 0.5 * Math.sin(a * dens.frequency - t * rate * dens.speed * TAU + 1.7));
      }

      /* ── The massif ───────────────────────────────────────────────────────
         What rises where you are pointing, on top of everything above. A group
         of summits rather than one peak: tallest in the middle — exactly on the
         marker — and falling away on both sides, each with its own width,
         height, lean, flanks and notches, all of it keyed to which marker it is,
         so every service has its own mountain and it is the same one every
         time. */
      const pillar = pillarRef.current;
      if (pillar.level <= 0.002) return;

      const peaks = TUNING.massifPeaks;
      // Each marker's own seed, so its massif is distinctive and unchanging.
      const seed = ((pillar.angle * 4096) | 0) * 31;
      for (let q = 0; q < peaks; q++) {
        const nq = seed + q * 101;
        /* Rank zero is the principal summit: it stands exactly on the marker,
           at full height, and nothing is allowed to move it. Everything after
           it is a shoulder, stepping away in pairs — right, left, right, left —
           and keeping a fixed share of the height of the rank inside it. */
        const rank = (q + 1) >> 1;
        const side = q === 0 ? 0 : q & 1 ? 1 : -1;
        const at =
          pillar.angle + side * (rank * TUNING.massifStep + noiseAt(nq, 0) * TUNING.massifJitter);

        const fall =
          q === 0
            ? 1
            : /* Varied downward only. Spread around one, a shoulder can come out
                 taller than the principal — rank one at eight tenths, times a
                 noise that reaches one and a quarter, clears it — and then the
                 marker is standing beside the tallest thing rather than under
                 it. Capped at one, no shoulder can ever win. */
              Math.pow(TUNING.massifFall, rank) *
              (1 - TUNING.spikeScaleRange + noiseAt(nq, 16) * TUNING.spikeScaleRange);
        /* Rank zero is a ridge — something with a base, for the rest to stand
           on. Outward from it they are needles and splinters, which is what
           gives the group its torn silhouette rather than a row of hills: the
           tall thin ones catch the eye and the low leaning ones fill between
           them. Each kind carries its own lobe count, sharpness, erosion, lean
           and catchment, so they are the same machinery and nothing alike. */
        const kindName = TUNING.massifRanks[Math.min(rank, TUNING.massifRanks.length - 1)]!;
        const kind = kinds[kindName] ?? kinds.mound!;
        const wu = noiseAt(nq, 8);
        /* Width comes from the kind, not from the rank. A tower and the dune it
           stands on are not the same shape at two sizes — one is a fifth of a
           base across and the other is three. */
        const w =
          (TUNING.spikeWidth + wu * wu * TUNING.spikeWidthRange) *
          kind.wide *
          (q === 0 ? TUNING.massifCrown : Math.pow(TUNING.massifNarrow, rank));
        const height = pillar.level * TUNING.spikeScale * fall;

        writeShape(q * META, kind, nq);
        const own = w * kind.claim;
        writeFormation(
          field,
          (at + TAU) * FIELD_K,
          own * TUNING.spikeCatch * (0.3 + 0.7 * fall) * FIELD_K,
          w,
          own,
          height,
          q === 0
            ? 0
            : (noiseAt(nq, 56) < 0.5 ? -1 : 1) * (0.25 + noiseAt(nq, 48) * 0.75) * kind.lean * w,
          q,
          1
        );
      }
    };

    /**
     * One frame of halo: the body of the smoke on its own small canvas, then
     * the dust on a larger one.
     *
     * They are separated because they want opposite things. The body wants to
     * be blurred until no one blob can be picked out, which is what the fifth-
     * size canvas does for free on the way to the screen. The dust wants the
     * opposite — a ridge of it should read as grain thrown off the ring, and
     * grain that has been blurred to a smudge is just more smoke. So it gets a
     * canvas of its own at three fifths, where a particle is still a particle.
     *
     * `prime` is for a frame with no history behind it — the first paint, or a
     * reader who asked for no motion. It clears instead of fading, and draws at
     * the strength the running total would have settled at, so a still ring
     * looks like a moving one that happens to be stopped.
     */
    const render = (t: number, step: number, prime = false) => {
      buildField(t);
      // The secondary pulse: the whole circumference easing out and back,
      // slower than anything travelling round it.
      /* The secondary pulse, and the one thing on the ring that is a pulse:
         the whole circumference easing out and back, slower than anything
         travelling round it, so the two never beat against each other. It grows
         and quickens with agitation like everything else. */
      const level = intensityRef.current;
      const gain = 1 + (level - TUNING.intensity) * TUNING.intensitySwing;
      const rate = 1 + (level - TUNING.intensity) * TUNING.intensityRate;
      const breath = Math.sin(t * rate * TUNING.smokeBreathRate * TAU) * TUNING.smokeBreath * gain;

      /* Take a bite out of the frame before rather than clearing it. What is
         left behind each particle is its own wake, and the halo you see is
         several frames deep — which is most of what makes this read as smoke
         moving rather than as points being redrawn. On canvases this small it
         costs nothing. */
      const wake = (c: CanvasRenderingContext2D, scale: number, keep: number) => {
        // Drawn in the container's coordinates throughout, even though the
        // canvas itself only covers the ring's square — so the geometry below
        // never has to know where its layer happens to start.
        c.setTransform(scale, 0, 0, scale, -boxX * scale, -boxY * scale);
        if (prime || keep <= 0) {
          c.clearRect(boxX, boxY, boxSide + 2, boxSide + 2);
          return;
        }
        c.globalCompositeOperation = 'destination-out';
        c.globalAlpha = keep;
        c.fillStyle = '#000';
        c.fillRect(boxX, boxY, boxSide + 2, boxSide + 2);
        c.globalCompositeOperation = 'source-over';
      };

      // ── The body ──────────────────────────────────────────────────────────
      wake(hctx, smokeScale, TUNING.smokeTrail);
      const bodyAlpha = TUNING.smokeAlpha * (prime ? 1 / TUNING.smokeTrail : 1);
      hctx.save();
      // The texture's own hard boundary — the halo may bloom past the ring
      // line, but never past this.
      hctx.beginPath();
      hctx.arc(cx, cy, clipR, 0, TAU);
      hctx.clip();

      for (let i = 0; i < smokeCount; i++) {
        const ni = i;
        const rate = 0.4 + noiseAt(ni, 128) * 0.8;

        /* Where it is on the ring: its own place in the band, how far it has
           flowed round since the page loaded, and a slow sideways sway on top.
           The flow wraps at a whole turn per particle, so one that has been
           going for an hour sits exactly where it would if it had just
           started — there is nothing to jump when it comes round.

           The extra turn keeps the angle positive whatever the sway does with
           it, which is what lets the table index be a truncation and a mask. */
        const flowRate = TUNING.smokeFlow * (0.6 + noiseAt(ni, 224) * 0.8);
        const swirl = Math.sin(t * rate * 0.61 + noiseAt(ni, 160) * TAU) * TUNING.smokeSwirl;
        const a = (i / smokeCount) * TAU + TAU + ((t * flowRate) % TAU) + swirl / radius;

        const fa = a * FIELD_K;
        const f0 = fa | 0;
        const fr = fa - f0;
        const o0 = (f0 & FIELD_MASK) * FIELD_STRIDE;
        const o1 = ((f0 + 1) & FIELD_MASK) * FIELD_STRIDE;
        const swell = field[o0 + F_SWELL] + (field[o1 + F_SWELL] - field[o0 + F_SWELL]) * fr;
        const peak = field[o0 + F_SUMMIT] + (field[o1 + F_SUMMIT] - field[o0 + F_SUMMIT]) * fr;
        /* …weighted by how heavy the haze is on this arc. The smooth stretches
           of the perimeter are quiet here as well as free of formations, which
           is what makes the disturbed arcs read as disturbed. */
        const density =
          (field[o0 + F_DENSITY] + (field[o1 + F_DENSITY] - field[o0 + F_DENSITY]) * fr) *
          zoneHaze[f0 & FIELD_MASK];

        /* Signed distance from the ring line: a scattered resting offset, this
           blob's own slow drift, and the swell lifting the whole arc. The swell
           is taken as 0..1 rather than -1..1 — an arc expands outward and comes
           back, it never pulls inward, and the middle of the circle stays
           empty. */
        const driftAmp = TUNING.smokeDrift + noiseAt(ni, 192) * TUNING.smokeDriftRange;
        const offset =
          noiseAt(ni, 64) * TUNING.smokeSpread -
          TUNING.smokeInset +
          Math.sin(t * rate + noiseAt(ni, 96) * TAU) * driftAmp +
          (swell * 0.5 + 0.5) * TUNING.smokeSwellPush +
          peak * maxOut * TUNING.smokeBodyLift;

        // Halo depth here and now: thicker where the swell has risen and
        // thicker again under a ridge, which is most of what makes an arc read
        // as energised rather than merely displaced.
        const band = Math.max(
          5,
          TUNING.smokeBand * (1 + swell * TUNING.smokeSwellDepth + peak * TUNING.smokePeakDepth)
        );

        /* Gaussian falloff: dense at the ring, thinning outward. On its own a
           gaussian never quite reaches zero, so it is multiplied by a
           smoothstep window that hits exactly zero at the safety clip — with
           a zero derivative there, so the halo can be broad and still meet the
           bound with nothing to cut. No visible edge, at any angle or phase. */
        const blob = TUNING.smokeBlob + noiseAt(ni, 32) * TUNING.smokeBlobRange;
        /* Faded to nothing a blob's width short of the bound, not at it. A blob
           is a sprite thirty pixels across drawn around its centre, so a centre
           that only just clears the safety arc still has most of itself over
           the far side of it — and what the arc then cuts is a straight line
           across the top of the halo. Invisible while the halo was faint, and
           the first thing you see once it is not. */
        const reach = Math.max(1, maxOut - blob * 0.8);
        const tail = Math.min(1, Math.max(0, 1 - Math.max(0, offset) / reach) / hold);
        const fade =
          density *
          (1 + peak * TUNING.smokePeakInk) *
          tail *
          tail *
          (3 - 2 * tail) *
          Math.exp(-(offset * offset) / (2 * band * band));
        if (fade < 0.008) continue;

        const r = radius + breath + offset;
        hctx.globalAlpha = bodyAlpha * fade;
        hctx.drawImage(
          sprite,
          cx + Math.cos(a) * r - blob,
          cy + Math.sin(a) * r - blob,
          blob * 2,
          blob * 2
        );
      }
      hctx.globalAlpha = 1;
      hctx.restore();

      /* ── The field, integrated ────────────────────────────────────────────
         One pass over one population. For each particle: work out where it is
         currently meant to be, pull it that way with a spring, push it off the
         cursor, move it, draw it. Everything the ring does that moves is in
         these forty lines, and none of it can teleport, because the only thing
         written to a position is a velocity.

         The order matters. The target is the band's own resting shape, then the
         field's drift on top of it, then — if this particle has been gathered —
         a slot in the column blended in by how far it has been taken. A
         particle half-taken is genuinely half way between the two, on its own
         path, with its own momentum. */
      // A typed-array fill is a memset; this is the whole clear.
      pixels.fill(0);

      const cur = cursorRef.current;
      const spring = TUNING.liveSpring;
      const damp = TUNING.liveDamp;
      const curR = TUNING.cursorRadius;
      const curR2 = curR * curR;
      const curPush = cur.on ? TUNING.cursorPush : 0;
      const flow = TUNING.liveFlow * step;

      for (let i = 0; i < liveCount; i++) {
        /* Turn slowly around the ring — except when held, because something
           being gathered is not also travelling past. */
        /* Everything turns, at the same rate, whether or not a formation has
           hold of it — and this is not a detail.

           It used to turn by `flow * (1 - held)`, on the reasoning that
           something being gathered into a peak is not also travelling past.
           The reasoning is right and the implementation was the wrong place
           for it: a particle's home angle is where it belongs in the band, and
           slowing that down for as long as a formation holds it means the ones
           inside the formation fall behind the ones outside it and never catch
           up. The gap does not heal, because nothing pulls a home angle back.
           Every hover left the band permanently bunched at the edges of
           wherever the peak had been, and the piles accumulated: measured over
           twelve hovers, the unevenness of the band's mass around the
           circumference went from 0.42 to 0.71, and the worst pile from under
           three times the average to nearly four.

           Nothing is lost by turning them all together, because a held
           particle does not go anywhere when its home does. Where it is aiming
           is `a + (centre - a) + slot`, and the `a` cancels: it is standing on
           the formation, at the place across the base that is its own, and the
           band can rotate underneath it all it likes. Only a part-held one
           drifts, by exactly the share of it the formation has not taken. */
        let a = homeA[i] + flow;
        if (a > TAU) a -= TAU;
        homeA[i] = a;

        // What the field is doing here.
        const fa = (a + TAU) * FIELD_K;
        const f0 = fa | 0;
        const fr = fa - f0;
        const o0 = (f0 & FIELD_MASK) * FIELD_STRIDE;
        const o1 = ((f0 + 1) & FIELD_MASK) * FIELD_STRIDE;
        const swell = field[o0 + F_SWELL] + (field[o1 + F_SWELL] - field[o0 + F_SWELL]) * fr;
        const density =
          field[o0 + F_DENSITY] + (field[o1 + F_DENSITY] - field[o0 + F_DENSITY]) * fr;
        /* The formation it is standing in, if any: how strongly it has hold,
           how high it stands, how wide its base is, and where its summit lies.
           Read from the nearer of the two entries rather than interpolated —
           these are one formation's properties, and half way between two of
           them is not a third formation. Only the hold is blended, because that
           is the one thing that has to have no edge. */
        const grab = field[o0 + F_GRAB] + (field[o1 + F_GRAB] - field[o0 + F_GRAB]) * fr;
        let summit = 0;
        let baseW = 0;
        let toCrest = 0;
        let which = 0;
        /* Only read when there is something to read. At rest — which is most of
           the time, and every particle of every frame while nothing is being
           pointed at — the hold is zero everywhere and these four loads are
           four loads of nothing, fifteen thousand times a frame. */
        if (grab > 0) {
          const nearer = fr < 0.5 ? o0 : o1;
          summit = field[nearer + F_SUMMIT];
          baseW = field[nearer + F_BASE];
          toCrest = field[nearer + F_TO_CREST];
          which = field[nearer + F_ID];
        }

        const climb = climbs[i];
        const wob = t * TUNING.liveDriftRate + climb * TAU * 3.1;

        /* ── Climbing ────────────────────────────────────────────────────────
           Its place across the formation's base is its own, fixed to it, and
           has nothing to do with the angle it was resting at — which is what
           lets a base be narrow while the ground it was piled out of is wide.

           The height it stands at is that place's height in the profile, times
           how far up this particle happens to be. The profile is the eroded,
           lopsided outline of this particular formation: one flank hollow, the
           other bulging, a few notches cut across both. So the silhouette of a
           formation full of particles is that outline, and no two are alike. */
        let lift = 0;
        let drawIn = 0;
        let fray = 0;
        /** How much of its resting scatter this one has left in the band. A
         *  particle on the surface leaves all of it there — a shell carrying a
         *  thirty-pixel spread is a fuzzy shell, and the whole point of it is
         *  to be an edge. */
        let shed = 0;
        /* Nothing at all unless this formation's hold beats this particle's own
           threshold. Below it a particle is not half-taken, it is untouched:
           still flowing, still in the band, still exactly where it was. */
        const take = grab > joins[i] ? grab : 0;
        /** How much of this particle the formation ends up with — the hold it
         *  has on the band here, except for the shell, which commits faster. */
        let hold = take;
        if (take > 0.004 && summit > 0.001) {
          const su = slotU[i];
          const au = su < 0 ? -su : su;
          const m = (which | 0) * META;
          /* The outline, as the tallest of this formation's lobes at this place
             across its base. Each lobe rises from its foot to a point, with a
             flank that caves inward on the way — and where two of them overlap
             only the taller shows, so between every pair the outline pinches
             into a notch instead of running on as one slope. Compared with
             their heights pre-raised, and put through the power once at the
             end, which costs one power a particle however many lobes there
             are. */
          const lobes = meta[m + M_COUNT];
          let cap = 0;
          for (let l = 0; l < lobes; l++) {
            const o = m + M_LOBES + l * LOBE;
            const t = (su - meta[o]) * meta[o + 1];
            const d = 1 - (t < 0 ? -t : t);
            if (d <= 0) continue;
            const v = meta[o + 2] * d;
            if (v > cap) cap = v;
          }
          /* Erosion, at two scales. One ripple across the base on its own is
             corrugation — an even scallop, the same bite taken every time,
             which reads as a decorated shape rather than as rock. The coarse
             term squared is what breaks that: it spends most of a flank near
             zero, leaving long clean slopes, and occasionally takes a deep bite
             out of one. The fine term crumbles whatever is left. */
          const phase = meta[m + M_PHASE];
          /* One wave, not two. The second was a fine crumble on top of the
             coarse bite, and it cost a sine for every particle on the ring now
             that the perimeter wears formations at rest rather than only under
             the pointer. Squared, the one that is left already spends most of a
             flank near zero and occasionally takes a deep bite, which is the
             part that reads. */
          const e2 = 0.5 + 0.5 * Math.sin(su * TUNING.spikeRipple * 0.37 - phase * 1.3);
          /* Tapered to nothing at the crest. Erosion that bites everywhere bites
             the summit too, and a notch cut across the highest point moves the
             highest point onto a flank — so the marker ends up beside its own
             mountain rather than under it. Summits are sharp and flanks are
             worked; this is that, and it is also what keeps the promise that
             what you are pointing at is the middle of what stands up. */
          const notch = 1 - meta[m + M_ERODE] * e2 * e2 * au;
          const profile = cap <= 0 ? 0 : Math.pow(cap, meta[m + M_POINT]) * notch;

          const role = roles[i];
          if (role === ROLE_MOTE) {
            /* Dust rather than slope. It is carried up past the crown and left
               there, circling slowly on its own phase, wider than the base it
               came out of and fainter than anything standing on the mountain.

               It never reaches a resting place: the cycle below has no end, so
               the air around a summit is always moving even when the summit
               itself has finished rising. And because a mote is only a particle
               that is currently being gathered, letting go puts it back in the
               band with everything else — the atmosphere has no separate
               lifetime to leak. */
            const ph = t * TUNING.moteRate + frays[i] * TAU;
            const hover =
              summit * maxOut * (TUNING.moteFloor + (1 - TUNING.moteFloor) * climb) +
              TUNING.moteLift * (0.5 + 0.5 * Math.sin(ph));
            lift = hover * take;
            drawIn = (toCrest + Math.sin(ph * 0.73 + su * TAU) * TUNING.moteWander * baseW) * take;
            fray = TUNING.peakFray * 0.5 * take;
            shed = 1;
          } else if (role === ROLE_CRUST) {
            /* On the surface, a little under it. The profile it follows is the
               same eroded, lopsided, notched outline the flanks are cut from,
               so what this draws is not a smooth shell over a rough mountain —
               it is the mountain's own silhouette, with every cavity and
               overhang in it. */
            const ct = take * TUNING.crustGrip;
            const grip = ct > 1 ? 1 : ct;
            lift =
              summit *
              profile *
              maxOut *
              (1 - (frays[i] < 0 ? -frays[i] : frays[i]) * TUNING.crustSink) *
              grip;
            drawIn = (toCrest + su * baseW) * grip;
            fray = TUNING.peakFray * 0.3 * grip;
            shed = 1;
            hold = grip;
          } else {
            lift = summit * profile * maxOut * climb * take;
            // Toward the summit, and then out across the base to its own place.
            drawIn = (toCrest + su * baseW) * take;
            /* How far off the clean line of the slope it has strayed. None at
               the base, where the formation is packed solid, and all of it at
               the summit — which is what leaves the dense root and the soft
               broken crown the reference has, instead of a triangle with an
               edge. */
            fray = climb * climb * TUNING.peakFray * take;
            shed = climb * TUNING.peakShed;
          }
        }
        const held = hold;

        /* Everything the band does to a particle where it was lying has to let
           go of it as the formation takes it, and all three of these were still
           being applied at full strength to particles that had already arrived
           on a summit.

           The swell is the worst of them: it is a function of the angle a
           particle came *from*, and a formation gathers from sixty degrees of
           arc into a few, so particles landing on the same point of the same
           outline were being pushed out by anything between nothing and six
           pixels each. Against a formation fifty pixels tall that is an outline
           ten pixels thick, which is not an outline. The drift terms do the
           same thing more gently. Faded out by the hold, what lands on the
           surface lands on the surface. */
        const loose = 1 - held;
        const off =
          homeR[i] * (1 - shed * held) +
          (swell * 0.5 + 0.5) * TUNING.smokeSwellPush * loose +
          lift +
          frays[i] * fray +
          Math.sin(wob) * TUNING.liveDrift * (1 - held * 0.6);

        const aim = a + drawIn + (slotU[i] * fray) / radius;
        const rr = radius + breath + off;
        const jig = TUNING.liveDrift * 0.5 * (1 - held * 0.7);
        const tx = cx + Math.cos(aim) * rr + Math.sin(wob * 0.7) * jig;
        const ty = cy + Math.sin(aim) * rr + Math.cos(wob * 0.9) * jig;

        // ── Forces ──────────────────────────────────────────────────────────
        let ax = (tx - pxs[i]) * spring;
        let ay = (ty - pys[i]) * spring;

        if (curPush > 0) {
          const dx = pxs[i] - cur.x;
          const dy = pys[i] - cur.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < curR2) {
            const d = Math.sqrt(d2) || 0.0001;
            /* Smoothstep on the distance: full strength at the middle, nothing
               at the edge, and a flat approach to both — so there is no ring
               anywhere, only particles that have moved more the nearer they
               are. */
            const q = 1 - d / curR;
            const w = q * q * (3 - 2 * q);
            const nx = dx / d;
            const ny = dy / d;
            /* Held particles barely feel it. The cursor sits on the marker, so
               the column forms exactly where the push is strongest — and a push
               that can move a particle forty pixels will take a column apart as
               fast as it is gathered. Whatever has been gathered is held by the
               column instead; that is what has hold of it. */
            const push = curPush * w * (1 - held * 0.9);
            // Off it, and around it. The tangential share is what makes the
            // field part and flow past rather than simply blow outward.
            ax += nx * push - ny * push * TUNING.cursorSwirl;
            ay += ny * push + nx * push * TUNING.cursorSwirl;
          }
        }

        // ── Integrate ───────────────────────────────────────────────────────
        let vx = vxs[i] + ax * step;
        let vy = vys[i] + ay * step;
        const dampf = 1 - damp * step;
        vx *= dampf;
        vy *= dampf;
        if (prime) {
          vx = 0;
          vy = 0;
          pxs[i] = tx;
          pys[i] = ty;
        } else {
          pxs[i] += vx * step;
          pys[i] += vy * step;
        }
        vxs[i] = vx;
        vys[i] = vy;

        /* Fade out anything that has been pushed past the bound, so the clip
           never has an edge to show. Measured from where the particle actually
           is, not from where it was told to be. */
        const ox = pxs[i] - cx;
        const oy = pys[i] - cy;
        const dist = Math.sqrt(ox * ox + oy * oy);
        const outward = dist - radius;
        const tail = Math.min(1, Math.max(0, 1 - Math.max(0, outward) / maxOut) / hold);
        const at = alphas[i] * density * tail * tail * (3 - 2 * tail) * (1 + held * (inks[i] - 1));
        if (at < 0.012) continue;

        /* Its ink goes across the four pixels it falls between, weighted by how
           much of each it covers, so a particle moving a third of a pixel moves
           a third of a pixel on screen rather than snapping. Size sets how much
           ink there is rather than how many pixels are touched: at around one
           pixel across that is the same picture, and it costs four writes
           whatever the size. */
        const fx = (pxs[i] - boxX) * liveScale - 0.5;
        const fy = (pys[i] - boxY) * liveScale - 0.5;
        const ix = fx | 0;
        const iy = fy | 0;
        if (fx < 0 || fy < 0 || ix >= bufW - 1 || iy >= bufH - 1) continue;
        const g = sizes[i];
        const ink = TUNING.liveAlpha * at * Math.min(1, g * g) * 255;
        const wx = fx - ix;
        const wy = fy - iy;
        const row = iy * bufW + ix;
        splat(row, ink * (1 - wx) * (1 - wy));
        splat(row + 1, ink * wx * (1 - wy));
        splat(row + bufW, ink * (1 - wx) * wy);
        splat(row + bufW + 1, ink * wx * wy);
      }
      uctx.putImageData(frame, 0, 0);
    };

    // Resume where the halo left off. Feeding the raw timestamp meant the
    // drift phase jumped forward by however long the ring had been off screen,
    // so scrolling back snapped the halo to a different shape instead of
    // continuing. The accumulated clock keeps it continuous across pauses.
    render(clockRef.current, 0, true);

    // Repaint on the next frame as well. The custom properties above can still
    // be unresolved during this pass on a cold load; by the next frame the
    // stylesheet has always been applied, so this is what guarantees the halo
    // ends up the right colour without waiting for a scroll or a resize.
    const settle = requestAnimationFrame(() => {
      /* …but only if it would come out different. The whole point of this frame
         is that the custom properties can still be unresolved during the pass
         above; if they resolved to the same strings, repainting tens of
         thousands of stamps changes nothing and costs the same as the first
         time. */
      const now = getComputedStyle(canvas);
      const grainNow =
        now.getPropertyValue('--m-ring-grain-rgb').trim() ||
        (isDark ? '210, 210, 206' : '30, 30, 30');
      if (grainNow !== grainRgb) paintBand();
      render(clockRef.current, 0, true);
    });

    // Only the smoke drifts, and only while visible and motion is allowed.
    if (!animated || prefersReducedMotion || !isOnScreen) {
      return () => {
        cancelAnimationFrame(settle);
        cancelAnimationFrame(bandRaf);
      };
    }

    let raf = 0;
    let last = 0;
    const gap = 1000 / TUNING.textureFps;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (!last) last = now;
      const dt = now - last;
      const pillar = pillarRef.current;
      const cursor = cursorRef.current;
      const moving =
        pillar.level !== pillar.target ||
        (cursor.on &&
          (Math.abs(cursor.tx - cursor.x) > 0.5 || Math.abs(cursor.ty - cursor.y) > 0.5));
      // Twenty a second is enough for smoke drifting. It is not enough for a
      // quarter-second rise: that is five frames and you can count them. So
      // while the column is moving, and only then, this runs at the display's
      // own rate.
      if (!moving && dt < gap) return;
      last = now;
      if (moving) {
        const step = dt / TUNING.pillarRiseMs;
        if (pillar.target > pillar.level)
          pillar.level = Math.min(pillar.target, pillar.level + step);
        else pillar.level = Math.max(pillar.target, pillar.level - step);
        // Down, with somewhere else to be: take the handover.
        if (pillar.level <= 0.001 && pillar.pending !== null) {
          pillar.angle = pillar.pending;
          pillar.pending = null;
          pillar.target = 1;
        }
      }
      /* Clamped. A tab that was in the background hands back a dt of several
         seconds, and a spring integrated over several seconds in one step does
         not settle, it explodes. */
      const step = Math.min(dt, 60) / 1000;
      // The influence point chases the pointer rather than being it, which is
      // most of what gives the interaction weight.
      const cur = cursorRef.current;
      const chase = Math.min(1, TUNING.cursorEase * step);
      cur.x += (cur.tx - cur.x) * chase;
      cur.y += (cur.ty - cur.y) * chase;
      clockRef.current += step;
      render(clockRef.current, step);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(settle);
      cancelAnimationFrame(bandRaf);
      cancelAnimationFrame(raf);
    };
  }, [
    layout,
    size.w,
    size.h,
    isMobile,
    animated,
    prefersReducedMotion,
    isOnScreen,
    noiseAt,
    schemeKey,
  ]);

  /* The pillar's angle and whether it should be up, pushed into the ref the
     frame loop reads. Nothing here triggers a repaint on its own — the loop is
     already running, and it picks the change up on its next frame. */
  useEffect(() => {
    const id = activeMarkerId ?? introBurstId;
    const target = services.find((s) => s.id === id);
    const pillar = pillarRef.current;
    if (!target) {
      pillar.target = 0;
      pillar.pending = null;
      return;
    }
    if (pillar.level <= 0.002) {
      // Down already: stand it where it is wanted and raise it.
      pillar.angle = target.angle;
      pillar.pending = null;
      pillar.target = 1;
    } else if (target.angle !== pillar.angle) {
      /* Still up, and wanted somewhere else. Sliding it round the ring would
         read as one column travelling; putting it down and raising another is
         what actually happened. So it is asked to fall, and where to stand
         next is remembered until it has. */
      pillar.pending = target.angle;
      pillar.target = 0;
    } else {
      pillar.target = 1;
    }
  }, [activeMarkerId, introBurstId, services]);

  // ── Topic selection, keyed by stable id ───────────────────────────────────
  const indexOfId = useCallback(
    (id: string | null) => (id == null ? -1 : services.findIndex((s) => s.id === id)),
    [services]
  );

  const scheduleAutoRotate = useCallback(() => {
    if (autoRotateTimerRef.current) clearTimeout(autoRotateTimerRef.current);
    autoRotateTimerRef.current = setTimeout(() => {
      autoRotateRef.current = true;
    }, 10000);
  }, []);

  const holdAutoRotate = useCallback(() => {
    autoRotateRef.current = false;
    if (autoRotateTimerRef.current) {
      clearTimeout(autoRotateTimerRef.current);
      autoRotateTimerRef.current = null;
    }
  }, []);

  const changeTopic = useCallback(
    (id: string) => {
      if (!id || id === activeIdRef.current) return;
      activeIdRef.current = id;
      if (prefersReducedMotion) {
        setDisplayId(id);
        return;
      }
      setTextOut(true);
      setTimeout(() => {
        setDisplayId(id);
        setTextOut(false);
      }, TUNING.hoverMs * 0.7);
    },
    [prefersReducedMotion]
  );

  /** Hover (mouse) and focus (keyboard) both drive the same marker + content
   *  emphasis, so the two interaction modes always produce the same result. */
  const setActiveMarker = useCallback(
    (id: string | null) => {
      if (!interactive) return;
      activeMarkerRef.current = id;
      setActiveMarkerId(id);
      if (!id) {
        autoRotateRef.current = false;
        scheduleAutoRotate();
        return;
      }
      holdAutoRotate();
      changeTopic(id);
    },
    [interactive, holdAutoRotate, scheduleAutoRotate, changeTopic]
  );

  // Auto-advance, but never out from under an active marker.
  useEffect(() => {
    if (!animated || prefersReducedMotion) return;
    const interval = setInterval(() => {
      if (!autoRotateRef.current || activeMarkerRef.current) return;
      const i = indexOfId(activeIdRef.current);
      changeTopic(services[(i + 1) % services.length]!.id);
    }, 7000);
    return () => clearInterval(interval);
  }, [animated, prefersReducedMotion, services, indexOfId, changeTopic]);

  /* ── The cursor ──────────────────────────────────────────────────────────
     Two things read the pointer. The marker's hit area takes a parallax nudge
     from it, which is React state and so is throttled to a frame; the particle
     field takes it as a force, which is a ref the frame loop reads and which
     must never cause a render. Both come off one listener.

     Only the ref is written on every event. State is written at most once a
     frame, and only while the value it would produce has actually changed. */
  useEffect(() => {
    if (isMobile || prefersReducedMotion || !animated) return;
    const el = containerRef.current;
    if (!el) return;
    let frame = 0;
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      cursor.tx = x;
      cursor.ty = y;
      if (!cursor.on) {
        // Arriving: start the influence where the pointer is rather than
        // sweeping it in from wherever it was left.
        cursor.x = x;
        cursor.y = y;
        cursor.on = true;
      }
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const nx = x / (r.width || 1) - 0.5;
        const ny = y / (r.height || 1) - 0.5;
        setParallax({ x: nx * TUNING.parallax, y: ny * TUNING.parallax });
      });
    };
    // Captured once: this ref holds a plain object, not a node React owns, so
    // it is the same object in the cleanup as it is here.
    const cursor = cursorRef.current;
    const onLeave = () => {
      cursor.on = false;
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', onLeave);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      cursor.on = false;
      if (frame) cancelAnimationFrame(frame);
    };
  }, [isMobile, prefersReducedMotion, animated]);

  const svc = services.find((s) => s.id === displayId) ?? services[0];
  const today = new Date();
  const dateCode = `${String(today.getMonth() + 1).padStart(2, '0')}.${String(
    today.getDate()
  ).padStart(2, '0')}.${String(today.getFullYear()).slice(-2)}`;

  // ── Connector: readout edge → elbow → point on the perimeter, with a fixed
  //    margin inside the ring so it always stops short of the boundary. ─────
  const connector = useMemo(() => {
    if (!layout || !svc || panelSize.w <= 0 || panelSize.h <= 0) return null;
    const target = pointOnCircle(layout, svc.angle);
    const dx = target.x - layout.cx;
    const dy = target.y - layout.cy;
    const dist = Math.hypot(dx, dy);
    if (dist === 0) return null;

    // Gap between the readout and the start of its connector, and the margin
    // the connector stops short of the ring. Both scale with the radius: as
    // fixed pixel values they ate the whole run on a smaller ring, and the
    // diagonal connectors fell under the minimum below and vanished.
    const panelGap = Math.max(10, layout.radius * 0.05);
    const ringMargin = Math.max(6, layout.radius * 0.035);
    const edge = rectRadiusAlong(panelSize.w / 2, panelSize.h / 2, dx / dist, dy / dist) + panelGap;
    const start = { x: layout.cx + (dx / dist) * edge, y: layout.cy + (dy / dist) * edge };
    const end = pointOnCircle(layout, svc.angle, -ringMargin);
    // Below this the elbow has no room to read as a connector, so it is
    // dropped rather than drawn as a stub.
    if (Math.hypot(end.x - start.x, end.y - start.y) < Math.max(14, layout.radius * 0.06)) {
      return null;
    }

    const pts = elbowConnector(start, end, Math.max(30, layout.radius * 0.15));
    if (!pts) return null;
    return { d: pointsToPath(pts), length: polylineLength(pts), end };
  }, [layout, svc, panelSize.w, panelSize.h]);

  const showRim = reached(phase, 'ring');
  const showGrain = reached(phase, 'grain');
  const showMarkers = reached(phase, 'markers');
  const showText = reached(phase, 'text');
  const motion = animated && !prefersReducedMotion;

  // ── Content block metrics — derived purely from the ring radius, never
  //    from the active topic's text, so the box is a fixed width AND height
  //    for a given size. Switching topics can never resize or reflow it. ───
  const contentMetrics = useMemo(() => {
    if (!layout) return null;
    const r = layout.radius;
    // Sizes were tuned to fit the box and ended up below reading size: on a
    // 1440px screen the description rendered at 11.2px and the outcome line at
    // 8px. Contrast was never the problem — 7.5:1 and 5.7:1 — but no contrast
    // makes 8px readable. Floors and coefficients both go up; the box height
    // below is derived from these, and the chord adapts, so the layout follows.
    const dateSize = clampPx(10, r * 0.028, 13);
    const labelSize = clampPx(9, r * 0.024, 12);
    const titleSize = clampPx(15, r * 0.075, 24);
    const descSize = clampPx(12, r * 0.058, 15);
    const outcomeSize = clampPx(10, r * 0.026, 12);
    const ctaSize = clampPx(11, r * 0.026, 13);
    const titleLines = 2;
    /* Fewer lines on the phone, not more.
   
       The compact layout used to take an extra description line, on the
       reasoning that a narrower box needs more of them — which is true, and is
       the wrong way round, because the box's height is what was making it
       narrow. Inside a circle the two trade against each other directly: every
       line added pushes the block's corners further up and down the arc and
       takes width off all of them. Dropping a line here and one from the
       outcome gives the width back, and at the width they get back the copy
       needs fewer lines in the first place. */
    const descLines = 3;
    const outcomeLines = isMobile ? 1 : 2;
    const height =
      dateSize * 1.3 +
      2 +
      labelSize * 1.3 +
      labelSize * 1.3 +
      8 +
      titleSize * 1.12 * titleLines +
      6 +
      descSize * 1.45 * descLines +
      10 +
      outcomeSize * 1.4 * outcomeLines +
      10 +
      ctaSize * 1.6;

    // A box of this height centred in the circle can be as wide as the chord
    // at its corners, which is far more than a flat fraction of the radius.
    // Small rings need that extra width or the copy wraps to two words a line
    // and clips; large rings keep the tighter fraction, which reads better.
    // The chord is taken against an inset radius so the block's corners keep
    // clear air between them and the ring, rather than crowding the arc.
    const halfH = height / 2;
    const inner = r * (1 - TUNING.contentEdgeInset[isMobile ? 'mobile' : 'desktop']);
    const chord = 2 * Math.sqrt(Math.max(0, inner * inner - halfH * halfH));
    const width = Math.min(chord, Math.max(r * TUNING.contentBoxRatio, TUNING.contentBoxMin));

    /* The box is centred on the ring, but the copy inside it is left-aligned,
       so every line short of the full measure leaves its slack on the right —
       and most of them are short: the date, the service code and the section
       labels use between a fifth and three quarters of it. Measured on a
       390px screen, the average line's centre falls 26px left of the box's,
       and the block reads as leaning into the left of the circle even though
       its box is exactly centred.

       Half that is given back. Correcting the whole 26px would centre the
       average line and push the two longest — which already come within 3px
       of the measure — hard against the arc on the right, trading a lean for
       a collision. Half moves the mass visibly without the widest line losing
       its clearance.

       Phones only. The same left-alignment holds at every size, but the ring
       is far larger relative to the copy on a desktop and the lean does not
       read. */
    const nudgeX = isMobile ? Math.round(width * 0.07) : 0;

    return {
      width,
      height,
      nudgeX,
      dateSize,
      labelSize,
      titleSize,
      descSize,
      outcomeSize,
      ctaSize,
      titleLines,
      descLines,
      outcomeLines,
    };
  }, [layout, isMobile]);

  // The largest radius any decorative element (tick, pulse, grain) is allowed
  // to reach before being hard-clipped — the literal "strict boundary."
  const clipRadius = layout ? layout.radius * (1 + TUNING.clipAllowance) : 0;

  return (
    <div
      ref={containerRef}
      role="region"
      aria-label="Noventi Labs service divergence timeline"
      onPointerLeave={(e) => {
        if (e.pointerType === 'mouse') setActiveMarker(null);
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: isMobile ? 'min(98vw, 440px)' : undefined,
        // Belt-and-suspenders: the SVG clipPath below is the real guarantee
        // that nothing crosses the ring; this just keeps any stray box-model
        // overflow (e.g. a focus ring at the very edge) from escaping too.
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes dt-signal {
          0%   { transform: scale(0.5); opacity: .7; }
          75%  { transform: scale(2.2); opacity: 0; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        @keyframes dt-signal-burst {
          0%   { transform: scale(0.5); opacity: .9; }
          70%  { transform: scale(3); opacity: 0; }
          100% { transform: scale(3); opacity: 0; }
        }
        @keyframes dt-draw { to { stroke-dashoffset: 0; } }
        @keyframes dt-turn { to { transform: rotate(360deg); } }
        @keyframes dt-counterturn { to { transform: rotate(-360deg); } }
        /* Sections fading up and back as the ring turns. It was the dash
           offset that did this, which looks the same and is not the same: a
           dash offset is a paint, and repainting an SVG the size of the hero on
           every frame cost more than the entire particle field. Opacity and
           transform are the two the compositor can do on its own. */
        @keyframes dt-segments {
          0%   { opacity: .35; }
          50%  { opacity: 1; }
          100% { opacity: .35; }
        }
        @keyframes dt-dot-in {
          from { transform: scale(0); opacity: 0; }
          to   { transform: scale(1); opacity: 1; }
        }
        @keyframes dt-rise {
          from { opacity: 0; transform: translateY(8px); filter: blur(5px); }
          to   { opacity: 1; transform: translateY(0);   filter: blur(0); }
        }
        .dt-rise { animation: dt-rise .6s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .dt-anim, .dt-rise { animation: none !important; }
        }
      `}</style>

      {/* Screen-reader list — the ring itself is decorative */}
      <ul className="sr-only">
        {services.map((s) => (
          <li key={s.id}>
            <a href={s.slug}>
              {s.title}: {s.shortDescription}
            </a>
          </li>
        ))}
      </ul>

      {/* Texture layers — the smoke halo, then the dense signal band over it.
          Three canvases rather than one, and each is the size it needs to be.
          The band never moves, so it is painted once per layout at full
          resolution and left alone. The body of the smoke wants to be blurred,
          so it gets a fifth-size canvas and lets the CSS upscale do it. The
          dust wants the opposite and gets three fifths, where a particle
          survives the trip to the screen. All three sit under the SVG, so the
          ring line, markers and connector stay crisp vectors. */}
      <canvas
        ref={hazeRef}
        aria-hidden="true"
        /* left/top are set from the layout when the canvas is sized — it
           covers the ring's square, not the whole container — so they are
           deliberately absent here for React to leave alone. */
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          opacity: showGrain ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 1.1s ease',
        }}
      />
      <canvas
        ref={dustRef}
        aria-hidden="true"
        /* left/top are set from the layout when the canvas is sized — it
           covers the ring's square, not the whole container — so they are
           deliberately absent here for React to leave alone. */
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          opacity: showGrain ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 1.1s ease',
        }}
      />
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        /* The rim arrives on the first beat rather than the second: it is the
           ring now, and the ring is what the sequence used to draw first. */
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          opacity: showRim ? 1 : 0,
          transition: prefersReducedMotion ? 'none' : 'opacity 1.3s ease',
        }}
      />

      {layout && (
        <svg
          width={size.w}
          height={size.h}
          viewBox={`0 0 ${size.w} ${size.h}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <defs>
            {/* The hard boundary: everything below is clipped to this circle
                (ring radius + a small allowance for tick marks and pulses),
                so no line, glow, or animated effect can ever cross it —
                regardless of stroke width, overshoot, or a math error. */}
            <clipPath id={clipId}>
              <circle cx={layout.cx} cy={layout.cy} r={clipRadius} />
            </clipPath>
          </defs>

          {/* ── Instrument rings ────────────────────────────────────────────
              Three hairlines in the margin outside the band, turning at
              different rates: two of them broken into segments that open and
              close as they go round, and one a ring of tiny ticks that reads as
              graduation rather than as a line.

              They are out here, and not inside the circle, because the circle's
              interior is where the readout sits. Concentric rings across the
              copy would be a watermark behind text; in the margin they are what
              they are meant to be — the instrument the perimeter belongs to.

              Deliberately faint. At this weight they are something you notice
              second, after the band, which is the order they should arrive
              in. */}
          <g
            className={motion ? 'dt-anim' : undefined}
            style={{
              opacity: showGrain ? 1 : 0,
              transition: 'opacity 1.4s ease .3s',
              transformOrigin: `${layout.cx}px ${layout.cy}px`,
              /* One turning group, not three turning circles.
   
                 Rotating an SVG element re-rasterises whatever it contains on
                 every frame, and three hairline dashed rings the size of the
                 hero cost more to repaint than the entire particle field —
                 measured at a sixth of the frame budget. Promoted to a single
                 layer the rotation is a compositor transform and the raster is
                 done once; what varies between the rings is their dash, their
                 radius and the phase of their fade, which are free. */
              willChange: motion ? 'transform' : undefined,
              animation: motion ? 'dt-turn 220s linear infinite' : undefined,
            }}
          >
            {[
              { r: 1.1, dash: '34 26', width: 0.5, alpha: 0.16, secs: 62 },
              { r: 1.17, dash: '7 21', width: 0.5, alpha: 0.12, secs: 91 },
              { r: 1.235, dash: '0.9 9', width: 0.9, alpha: 0.14, secs: 0 },
            ].map((ring) => (
              <circle
                key={ring.r}
                className={motion ? 'dt-anim' : undefined}
                cx={layout.cx}
                cy={layout.cy}
                r={layout.radius * ring.r}
                fill="none"
                stroke={`rgba(var(--m-ring-rgb),${ring.alpha})`}
                strokeWidth={ring.width}
                strokeLinecap="round"
                strokeDasharray={ring.dash}
                style={
                  motion && ring.secs
                    ? { animation: `dt-segments ${ring.secs}s ease-in-out infinite alternate` }
                    : undefined
                }
              />
            ))}
          </g>

          <g clipPath={`url(#${clipId})`}>
            {/* There is no ring drawn here any more.

                There were two circles: an exact one at the radius, and a faint
                dashed companion just inside it. Between them they were the ring,
                and the particles were decoration around a line. That is the
                thing this reads as when you look at it — a drawn circle with
                grit on it — and it is not what the reference is. So they are
                gone, and the perimeter is the particle layers underneath and
                nothing else.

                `layout.radius` is still exactly what it was. It is the geometry
                every marker, tick, connector and clip below is measured from;
                it simply no longer has a stroke on it. */}

            {/* Service markers: anchor point, radial tick, expanding signal
                ring, staggered pulse, small index label. Every topic gets
                the same treatment — only the active one is emphasized. */}
            <g style={{ opacity: showMarkers ? 1 : 0, transition: 'opacity .9s ease .2s' }}>
              {services.map((s, i) => {
                const { x, y } = pointOnCircle(layout, s.angle);
                const tickOuter = pointOnCircle(layout, s.angle, 9);
                const labelPoint = pointOnCircle(layout, s.angle, 20);
                const on = s.id === displayId;
                const burst = s.id === introBurstId;
                return (
                  <g key={s.id}>
                    {/* radial tick */}
                    <line
                      x1={x}
                      y1={y}
                      x2={tickOuter.x}
                      y2={tickOuter.y}
                      stroke={on ? 'rgba(var(--m-ring-rgb),.7)' : 'rgba(var(--m-ring-rgb),.4)'}
                      strokeWidth={0.8}
                      style={{ transition: `stroke ${TUNING.hoverMs}ms ${TUNING.hoverEasing}` }}
                    />
                    {/* expanding signal ring — ambient pulse, clipped like
                        everything else so it can never read as "escaping" */}
                    <circle
                      className={motion ? 'dt-anim' : undefined}
                      cx={x}
                      cy={y}
                      r={4}
                      fill="none"
                      stroke={on ? 'rgba(var(--m-ring-rgb),.55)' : 'rgba(var(--m-ring-rgb),.3)'}
                      strokeWidth={0.9}
                      style={
                        motion
                          ? {
                              transformOrigin: `${x}px ${y}px`,
                              animation: `${burst ? 'dt-signal-burst' : 'dt-signal'} ${TUNING.pulseSeconds}s cubic-bezier(.2,.6,.3,1) ${i * TUNING.pulseStagger}s infinite`,
                              transition: `stroke ${TUNING.hoverMs}ms ${TUNING.hoverEasing}`,
                            }
                          : { transition: `stroke ${TUNING.hoverMs}ms ${TUNING.hoverEasing}` }
                      }
                    />
                    {/* anchor dot */}
                    <circle
                      cx={x}
                      cy={y}
                      r={on ? 3 : 2}
                      fill={on ? 'rgba(var(--m-ring-rgb),1)' : 'rgba(var(--m-ring-rgb),.6)'}
                      style={{
                        transition: `r ${TUNING.hoverMs}ms ${TUNING.hoverEasing}, fill ${TUNING.hoverMs}ms ${TUNING.hoverEasing}`,
                      }}
                    />
                    {/* index label */}
                    <text
                      x={labelPoint.x}
                      y={labelPoint.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontFamily="ui-monospace, monospace"
                      fontSize={Math.max(7, layout.radius * 0.02)}
                      fill={on ? 'rgba(var(--m-ring-rgb),.7)' : 'rgba(var(--m-ring-rgb),.35)'}
                      style={{
                        userSelect: 'none',
                        transition: `fill ${TUNING.hoverMs}ms ${TUNING.hoverEasing}`,
                      }}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </text>
                  </g>
                );
              })}
            </g>

            {/* Connector: readout edge → elbow → active marker. Re-keyed per
                topic so it re-draws (stroke-dashoffset) on every switch — a
                fast, GPU-cheap "signal traveling out" read, well within the
                ring at every step of the animation, not just at rest. */}
            {connector && (
              <g style={{ opacity: showText ? 1 : 0, transition: 'opacity .4s ease' }}>
                <path
                  key={`${displayId}-${Math.round(layout.radius)}`}
                  className={motion ? 'dt-anim' : undefined}
                  d={connector.d}
                  fill="none"
                  stroke="rgba(var(--m-ring-rgb),0.7)"
                  strokeWidth="1"
                  strokeLinejoin="miter"
                  style={
                    motion
                      ? {
                          strokeDasharray: connector.length,
                          strokeDashoffset: connector.length,
                          animation: `dt-draw ${TUNING.hoverMs}ms ${TUNING.hoverEasing} forwards`,
                        }
                      : undefined
                  }
                />
                <circle
                  key={`${displayId}-dot`}
                  className={motion ? 'dt-anim' : undefined}
                  cx={connector.end.x}
                  cy={connector.end.y}
                  r="2"
                  fill="rgba(var(--m-ring-rgb),.7)"
                  style={{
                    transformOrigin: `${connector.end.x}px ${connector.end.y}px`,
                    animation: motion
                      ? `dt-dot-in ${TUNING.hoverMs}ms ${TUNING.hoverEasing} both`
                      : undefined,
                  }}
                />
              </g>
            )}
          </g>
        </svg>
      )}

      {/* Interactive areas — one per topic, positioned from the live layout */}
      {layout &&
        services.map((s, i) => {
          const centre = pointOnCircle(layout, s.angle);
          const x = centre.x + (s.id === activeMarkerId ? parallax.x : 0);
          const y = centre.y + (s.id === activeMarkerId ? parallax.y : 0);
          const box = Math.max(hitRadius * 2, 44);
          return (
            <button
              key={s.id}
              ref={(el) => {
                markerButtonRefs.current[i] = el;
              }}
              data-topic-id={s.id}
              className="absolute rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-800"
              style={{
                left: x - box / 2,
                top: y - box / 2,
                width: box,
                height: box,
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'crosshair',
                pointerEvents: interactive ? 'auto' : 'none',
                zIndex: s.id === activeMarkerId ? 3 : 2,
                transition: `left ${TUNING.hoverMs}ms ${TUNING.hoverEasing}, top ${TUNING.hoverMs}ms ${TUNING.hoverEasing}`,
              }}
              aria-label={`${s.title}: ${s.shortDescription}`}
              onPointerEnter={(e) => {
                lastPointerTypeRef.current = e.pointerType;
                if (e.pointerType === 'mouse') setActiveMarker(s.id);
              }}
              onPointerLeave={(e) => {
                if (e.pointerType === 'mouse') setActiveMarker(null);
              }}
              onPointerDown={(e) => {
                lastPointerTypeRef.current = e.pointerType;
                preTapActiveIdRef.current = activeMarkerRef.current;
                holdAutoRotate();
                setActiveMarker(s.id);
              }}
              onFocus={() => {
                holdAutoRotate();
                setActiveMarker(s.id);
              }}
              onBlur={() => {
                setActiveMarker(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  go(s.slug);
                }
                if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  e.preventDefault();
                  holdAutoRotate();
                  // Move real DOM focus (not just active state) so screen-reader
                  // focus, the visible focus ring, and the displayed content
                  // all agree on which marker is current.
                  markerButtonRefs.current[(i + 1) % services.length]?.focus();
                }
                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  holdAutoRotate();
                  markerButtonRefs.current[(i - 1 + services.length) % services.length]?.focus();
                }
              }}
              onClick={(e) => {
                // Touch: the first tap only activates/previews the service —
                // it must not fire navigation before the visitor has seen it.
                // A second tap on an already-active marker, or any mouse
                // click (which already previewed on hover), proceeds. Compare
                // against the pre-tap snapshot, not current state — React may
                // have already applied this tap's activation by the time
                // click fires, which would make a live-state check useless.
                if (lastPointerTypeRef.current === 'touch' && preTapActiveIdRef.current !== s.id) {
                  e.preventDefault();
                  return;
                }
                go(s.slug);
              }}
            />
          );
        })}

      {/* Centre content — the live service readout, connected to the ring.
          The block's own box is centered on the ring (horizontally and
          vertically) via the transform below; the text inside it is left-
          aligned, per the reference design. Width AND height are fixed for a
          given radius (contentMetrics), so switching topics never resizes or
          reflows this box — only the text inside it changes. */}
      {layout && contentMetrics && (
        <div
          ref={panelRef}
          style={{
            position: 'absolute',
            // Off the ring's centre by the optical correction above, not by a
            // change to the geometry: the ring, its markers and the chord the
            // box is measured against all still work from layout.cx.
            left: layout.cx + contentMetrics.nudgeX,
            top: layout.cy,
            transform: 'translate(-50%, -50%)',
            width: contentMetrics.width,
            height: contentMetrics.height,
            textAlign: 'left',
            pointerEvents: 'none',
            zIndex: 4,
          }}
        >
          <div
            className={showText && motion ? 'dt-rise' : undefined}
            style={{
              opacity: showText ? (textOut ? 0 : 1) : 0,
              transform: textOut ? 'translateY(-4px)' : undefined,
              transition: prefersReducedMotion
                ? 'none'
                : `opacity ${TUNING.hoverMs}ms ${TUNING.hoverEasing}, transform ${TUNING.hoverMs}ms ${TUNING.hoverEasing}`,
            }}
            aria-live="polite"
            aria-atomic="true"
          >
            {/* Date / index */}
            <p
              className="font-mono tracking-widest"
              style={{
                fontSize: contentMetrics.dateSize,
                color: 'var(--m-ink-5)',
                letterSpacing: '0.15em',
                marginBottom: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {dateCode}
            </p>
            {/* Category label */}
            <p
              className="font-mono uppercase tracking-widest"
              style={{
                fontSize: contentMetrics.labelSize,
                color: 'var(--m-ink-4)',
                letterSpacing: isMobile ? '0.06em' : '0.12em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Anomaly Signal Detected
            </p>
            <p
              className="font-mono uppercase tracking-widest"
              style={{
                fontSize: contentMetrics.labelSize,
                color: 'var(--m-ink-4)',
                letterSpacing: '0.1em',
                marginBottom: 8,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {svc?.systemCode}
            </p>
            {/* Topic title */}
            <h3
              className="uppercase font-bold"
              style={{
                fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                fontSize: contentMetrics.titleSize,
                color: 'var(--m-ink)',
                lineHeight: 1.12,
                letterSpacing: '0.04em',
                marginBottom: 6,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: contentMetrics.titleLines,
                overflow: 'hidden',
              }}
            >
              {svc?.title}
            </h3>
            {/* Description */}
            <p
              style={{
                fontFamily: "var(--font-plus-jakarta-sans), 'Plus Jakarta Sans', sans-serif",
                fontSize: contentMetrics.descSize,
                color: 'var(--m-ink-5)',
                lineHeight: 1.45,
                marginBottom: 10,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: contentMetrics.descLines,
                overflow: 'hidden',
              }}
            >
              {svc?.shortDescription}
            </p>
            {/* Supporting text */}
            <p
              className="font-mono uppercase"
              style={{
                fontSize: contentMetrics.outcomeSize,
                color: 'var(--m-ink-6)',
                letterSpacing: '0.08em',
                lineHeight: 1.4,
                marginBottom: 10,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: contentMetrics.outcomeLines,
                overflow: 'hidden',
              }}
            >
              {svc?.businessOutcome}
            </p>
            {/* Call to action */}
            <a
              href={svc?.slug}
              className="inline-flex items-center font-mono uppercase"
              style={{
                pointerEvents: 'auto',
                fontSize: contentMetrics.ctaSize,
                letterSpacing: '0.14em',
                color: 'var(--m-ink-2)',
                borderBottom: '1px solid rgba(var(--m-ring-rgb),0.4)',
                textDecoration: 'none',
                // This is the only link inside the ring and it was 14px tall,
                // well under the 24px minimum target. The box grows without the
                // text growing, so the card's layout is untouched.
                minHeight: 26,
                paddingBottom: 2,
              }}
            >
              [ EXPLORE SERVICE ]
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
